
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { getRedirectResult, onAuthStateChanged, signOut } from 'firebase/auth';
import { 
  collection, onSnapshot, query, where, addDoc, updateDoc, doc, 
  deleteDoc, getDoc, setDoc, getDocs, orderBy, or
} from 'firebase/firestore';
import { auth, db } from './services/firebase';
import type { 
  User, Recipe, Household, MealPlan, ShoppingCart, 
  ActiveShoppingCart, PlannedMeal, Role, HouseholdRole, 
  MealType, Ingredient, Suggestion, SubscriptionStatus,
  DayOfWeek, ActiveCartItem, ShoppingCategory, ThemeType,
  Chef, ChefRequest, UserMealPreferences, UpdateActiveShoppingCartFn,
  ConsultantClientContext, Language, UserNotification,
} from './types';
import Login from './components/Login';
import LandingPage from './components/LandingPage';
import GuidedAccessLogin from './components/GuidedAccessLogin';
import CreateGuidedClientModal from './components/CreateGuidedClientModal';
import GuidedClientManagementModal from './components/GuidedClientManagementModal';
import ConsultationModal from './components/ConsultationModal';
import ConsultantBanner from './components/ConsultantBanner';
import ConsultantPhraseAdminModal from './components/ConsultantPhraseAdminModal';
import GoProModal from './components/GoProModal';
import Header from './components/Header';
import BottomNav from './components/BottomNav';
import RecipeManager from './components/RecipeManager';
import WeeklyPlanner from './components/WeeklyPlanner';
import MonthlyPlanner from './components/MonthlyPlanner';
import ListTab from './components/ListTab';
import AddRecipeModal from './components/AddRecipeModal';
import RecipeDetailsModal from './components/RecipeDetailsModal';
import AddShoppingCartModal from './components/AddShoppingCartModal';
import ShoppingCartDetailsModal from './components/ShoppingCartDetailsModal';
import ActiveShoppingCartModal from './components/ActiveShoppingCartModal';
import AddMealModal from './components/AddMealModal';
import PlanScreen from './components/PlanScreen';
import UserManagementModal from './components/UserManagementModal';
import HouseholdManagementModal from './components/HouseholdManagementModal';
import SubscriptionManagementModal from './components/SubscriptionManagementModal';
import SettingsModal from './components/SettingsModal';
import PersonalDetailsModal from './components/PersonalDetailsModal';
import SuggestionModal from './components/SuggestionModal';
import SuggestionManagementModal from './components/SuggestionManagementModal';
import ContactModal from './components/ContactModal';
import HowToModal from './components/HowToModal';
import InfoNoticeModal from './components/InfoNoticeModal';
import TermsOfServiceModal from './components/TermsOfServiceModal';
import Reminders from './components/Reminders';
import NotificationsModal from './components/NotificationsModal';
import MealCookingSession from './components/MealCookingSession';
import PlannedMealRecipeView from './components/PlannedMealRecipeView';
import CookieConsent from './components/CookieConsent';
import InstallAppPrompt from './components/InstallAppPrompt';
import ChefAssistantModal from './components/ChefAssistantModal';
import ChefProfileModal from './components/ChefProfileModal';
import ChefEditModal from './components/ChefEditModal';
import ChefRequestModal from './components/ChefRequestModal';
import ChefManagementModal, { buildChefFromRequest } from './components/ChefManagementModal';
import { mergeChefsInFirestore, formatMergeSummary } from './utils/chefMergeService';
import ImpersonationBanner from './components/ImpersonationBanner';
import { DAYS_OF_WEEK, DAY_TRANSLATIONS } from './types';
import { getTranslation, isRTL } from './utils/translations';
import { getStartupLanguage, persistAppLanguage } from './utils/appLanguage';
import { resolveInstagramChef } from './utils/resolveInstagramChef';
import type { InstagramOwner } from './services/geminiService';
import { detectCategory } from './utils/unitConversion';
import { pillarToShoppingCategory, normalizeShoppingCategory, isPillarId, buildListPillars, normalizeCategoryAssignment } from './utils/shoppingCategories';
import { getPersonalHouseholdId, resolveActiveHouseholdId } from './utils/householdUtils';
import { getDefaultScheduleSlot } from './utils/mealScheduleDefaults';
import { buildCookingSessionDocId, mealBelongsToSlot } from './utils/cookingSessionKeys';
import { buildMealSlotTitleKey } from './utils/mealSlotTitle';
import { ensurePersonalHousehold } from './utils/ensurePersonalHousehold';
import { provisionNewUser } from './utils/provisionUser';
import { canUserCreateHousehold, getUserHouseholdLimit } from './utils/userHouseholdLimit';
import {
  toggleFavoriteRecipe,
  toggleFavoriteChef,
  syncPreferredDietFromUser,
} from './utils/userMealPreferences';
import { applyFontSize, getFontSizeMode, getFontSizeMultiplier } from './utils/fontSize';
import { countNewSuggestions, countPendingSubscriptionApprovals } from './utils/adminNotifications';
import { stripUndefinedFields, recipeUpdatePayload } from './utils/firestorePayload';
import { constrainTemporaryRecipeVisibility } from './utils/temporaryRecipe';
import { isRecipeSavePending, isStalePendingRecipe } from './utils/recipeSaveStatus';
import {
  buildFinalizeInputFromRecipe,
  finalizePendingRecipe,
} from './services/finalizePendingRecipe';
import { buildDefrostReminders, getWeekKey } from './utils/defrostReminders';
import { buildKnownItems } from './utils/knownItems';
import { activeCartSnapshotKey } from './utils/activeCartSnapshot';
import {
  clearPendingShareFromStorage,
  captureShareLaunchFromLocation,
  extractShareUrl,
  readPendingShareFromStorage,
  type ShareLaunchPayload,
} from './utils/sharePayload';
import { findConflictingDiscoverableRecipeForUrl } from './utils/recipeExternalVideoDuplicate';
import { requestServerShareImport } from './services/shareImportApi';
import { logShareImportEvent } from './services/shareImportLogService';
import {
  createUserNotification,
  markAllNotificationsRead,
  markNotificationRead,
} from './services/userNotificationService';
import { useUserNotifications } from './hooks/useUserNotifications';
import runningVersionData from './version.json';
import {
  clearGuidedSession,
  isGuidedUser,
  canGuidedUserViewRecipe,
  parseAccessTokenFromPathname,
  readGuidedSession,
  writeGuidedSession,
} from './utils/accountMode';
import { isNutritionConsultant } from './utils/userType';
import { buildDuplicateRecipeForHousehold } from './utils/duplicateRecipeForHousehold';
import {
  getConsultantSwitchableHouseholds,
  isGuidedClientHousehold,
  resolveConsultantActiveHouseholdId,
  canAccessGuidedClientsAdmin,
} from './utils/guidedClientHousehold';
import { buildResidentsUpdate } from './utils/householdResidents';
import { changeGuidedClientEmail, updateGuidedClientSelf } from './services/guidedClientService';
import { useConsultantBannerData } from './hooks/useConsultantBannerData';
import { useIntermittentFasting } from './hooks/useIntermittentFasting';
import { touchHouseholdPlanMeta } from './utils/householdPlanMeta';
import FastingTrackerModal from './components/fasting/FastingTrackerModal';
import FastingStatisticsModal from './components/FastingStatisticsModal';
import { isIntermittentFastingEnabled } from './utils/intermittentFastingUser';

function App() {
  // User State
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const t = (key: any) => getTranslation(user?.language || 'en', key);
  const [impersonatedUserId, setImpersonatedUserId] = useState<string | null>(null);
  const isImpersonating = impersonatedUserId !== null;
  const isImpersonatingRef = useRef(false);
  const pendingHouseholdSwitchRef = useRef<string | null>(null);
  const householdDataScopeRef = useRef(0);
  const pendingRecipeFinalizeRef = useRef<Set<string>>(new Set());
  const shareImportRunningRef = useRef(false);
  const chefsRef = useRef<Chef[]>([]);
  const [authLoading, setAuthLoading] = useState(true);
  const [authInitError, setAuthInitError] = useState<string | null>(null);
  const [pendingAccessToken, setPendingAccessToken] = useState<string | null>(() =>
    parseAccessTokenFromPathname(window.location.pathname),
  );
  const [useStandardLogin, setUseStandardLogin] = useState(false);
  const [consultantClientContext, setConsultantClientContext] = useState<ConsultantClientContext | null>(null);
  const [activeHouseholdId, setActiveHouseholdId] = useState<string>('');
  
  // Data State
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [households, setHouseholds] = useState<Household[]>([]);
  const [mealPlan, setMealPlan] = useState<MealPlan>({});
  const [reminderMealPlans, setReminderMealPlans] = useState<Record<string, MealPlan>>({});
  const [shoppingCarts, setShoppingCarts] = useState<ShoppingCart[]>([]);
  const [activeCart, setActiveCart] = useState<ActiveShoppingCart | null>(null);
  const activeCartRef = useRef<ActiveShoppingCart | null>(null);
  const [allUsers, setAllUsers] = useState<User[]>([]); // For admin
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [chefs, setChefs] = useState<Chef[]>([]);

  useEffect(() => {
    chefsRef.current = chefs;
  }, [chefs]);

  const [chefRequests, setChefRequests] = useState<ChefRequest[]>([]);
  const [userMealPreferences, setUserMealPreferences] = useState<UserMealPreferences | null>(null);
  const [categoryOverrides, setCategoryOverrides] = useState<Record<string, string>>({});

  // UI State
  const [showLogin, setShowLogin] = useState(() => {
    return window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
  });
  const [activeTab, setActiveTab] = useState<'plan' | 'recipes' | 'lists'>('plan');
  const [weekOffset, setWeekOffset] = useState(0);
  const [pendingChefFilterId, setPendingChefFilterId] = useState<string | null>(null);
  
  // Modals
  const [isAddRecipeOpen, setIsAddRecipeOpen] = useState(false);
  const [isChefAssistantOpen, setIsChefAssistantOpen] = useState(false);
  const [recipeToEdit, setRecipeToEdit] = useState<Recipe | undefined>(undefined);
  const [recipeToView, setRecipeToView] = useState<Recipe | null>(null);
  // True when the recipe preview was launched on top of the scheduler (so the scheduler should hide behind it)
  const [previewFromScheduler, setPreviewFromScheduler] = useState(false);
  
  const [isAddCartOpen, setIsAddCartOpen] = useState(false);
  const [cartToEdit, setCartToEdit] = useState<ShoppingCart | undefined>(undefined);
  const [cartToView, setCartToView] = useState<ShoppingCart | null>(null);
  
  const [isActiveCartOpen, setIsActiveCartOpen] = useState(false);
  
  const [addMealData, setAddMealData] = useState<{
      recipe: Recipe, 
      day: DayOfWeek, 
      type: MealType, 
      offset?: number,
      plannedMealId?: string,
      currentServings?: number,
      currentStatus?: 'buy' | 'pantry' | 'prepared',
      originalDay?: DayOfWeek,
      originalOffset?: number,
      originalType?: MealType,
  } | null>(null);

  const [plannedRecipePreview, setPlannedRecipePreview] = useState<{
      recipe: Recipe;
      plannedServings: number;
      day: DayOfWeek;
      mealType: MealType;
      weekOffset: number;
  } | null>(null);
  
  // Management Modals
  const [isUserManagementOpen, setIsUserManagementOpen] = useState(false);
  const [isCreateGuidedClientOpen, setIsCreateGuidedClientOpen] = useState(false);
  const [isGuidedClientsOpen, setIsGuidedClientsOpen] = useState(false);
  const [isConsultationOpen, setIsConsultationOpen] = useState(false);
  const [isPhraseAdminOpen, setIsPhraseAdminOpen] = useState(false);
  const [isGoProOpen, setIsGoProOpen] = useState(false);
  const [isHouseholdManagementOpen, setIsHouseholdManagementOpen] = useState(false);
  const [isSubscriptionManagementOpen, setIsSubscriptionManagementOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isPersonalDetailsOpen, setIsPersonalDetailsOpen] = useState(false);
  const [isSuggestionBoxOpen, setIsSuggestionBoxOpen] = useState(false);
  const [isSuggestionManagementOpen, setIsSuggestionManagementOpen] = useState(false);
  const [isChefManagementOpen, setIsChefManagementOpen] = useState(false);
  const [isChefRequestOpen, setIsChefRequestOpen] = useState(false);
  const [chefToView, setChefToView] = useState<Chef | null>(null);
  const [linkedChefToEdit, setLinkedChefToEdit] = useState<Chef | null>(null);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isHowToOpen, setIsHowToOpen] = useState(false);
  const [isTosOpen, setIsTosOpen] = useState(false);
  const [isRemindersOpen, setIsRemindersOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isFastingTrackerModalOpen, setIsFastingTrackerModalOpen] = useState(false);
  const [isFastingStatisticsOpen, setIsFastingStatisticsOpen] = useState(false);
  const [bootLang, setBootLang] = useState<Language>(() => getStartupLanguage());
  const [cookingSessionData, setCookingSessionData] = useState<{
    meals: (PlannedMeal & { recipe: Recipe })[];
    type: MealType;
    day?: DayOfWeek;
    weekOffset?: number;
    slotMenuTitles?: Partial<Record<string, string>>;
    initialRecipeId?: string;
  } | null>(null);
  const [scheduledConflict, setScheduledConflict] = useState<{ recipeName: string; day: DayOfWeek; mealType: MealType } | null>(null);
  const [genericNotice, setGenericNotice] = useState<{ title: string; message?: string; variant?: 'success' | 'info' | 'error' } | null>(null);
  const [invitedHouseholds, setInvitedHouseholds] = useState<Household[]>([]);

  const fastingUserId = user?.uid ?? authUser?.uid ?? '';
  const showFastingUi = isIntermittentFastingEnabled(user);
  const consultantBannerData = useConsultantBannerData(user, activeHouseholdId, user?.language);
  const canManagePhrases = user ? canAccessGuidedClientsAdmin(user) : false;
  const intermittentFasting = useIntermittentFasting(fastingUserId, {
    enabled: showFastingUi,
    language: user?.language,
  });
  const { notifications, unreadCount: unreadNotificationCount } = useUserNotifications(user?.uid);

  useEffect(() => {
    isImpersonatingRef.current = isImpersonating;
  }, [isImpersonating]);

  // Version change detection & auto-refresh
  useEffect(() => {
    const checkVersion = async () => {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          if (data.version && data.version !== runningVersionData.version) {
            console.log(`App version advanced from ${runningVersionData.version} to ${data.version}. Refreshing...`);
            window.location.reload();
          }
        }
      } catch (err) {
        console.error('Failed to check app version:', err);
      }
    };

    // Check on mount
    checkVersion();

    // Check every 5 minutes
    const interval = setInterval(checkVersion, 5 * 60 * 1000);

    // Check when user focuses back on the tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleImpersonateUser = useCallback((targetUser: User) => {
    if (authUser?.role !== 'superuser' || targetUser.uid === authUser.uid || targetUser.role === 'superuser') {
      return;
    }
    setImpersonatedUserId(targetUser.uid);
    setUser(targetUser);
    setActiveHouseholdId(resolveActiveHouseholdId(targetUser));
    setIsUserManagementOpen(false);
  }, [authUser]);

  const handleExitImpersonation = useCallback(() => {
    if (!authUser) return;
    setImpersonatedUserId(null);
    setUser(authUser);
    setActiveHouseholdId(resolveActiveHouseholdId(authUser));
  }, [authUser]);

  const handleSwitchHousehold = useCallback(async (householdId: string) => {
    if (!user) return;

    const targetHousehold = households.find((h) => h.id === householdId);
    if (
      isGuidedClientHousehold(targetHousehold ?? {})
      && canAccessGuidedClientsAdmin(user)
      && consultantClientContext?.householdId !== householdId
    ) {
      return;
    }
    if (consultantClientContext && consultantClientContext.householdId !== householdId) {
      setConsultantClientContext(null);
    }

    const updates: Partial<User> = { activeHouseholdId: householdId };
    const membershipIds = user.households || [];
    if (!membershipIds.includes(householdId)) {
      updates.households = [...membershipIds, householdId];
    }

    pendingHouseholdSwitchRef.current = householdId;
    setActiveHouseholdId(householdId);
    const nextUser = { ...user, ...updates };
    setUser(nextUser);
    if (!isImpersonating && authUser) {
      setAuthUser({ ...authUser, ...updates });
    }

    try {
      await updateDoc(doc(db, 'users', user.uid), updates);
    } catch (err) {
      console.error('Failed to switch household', err);
      pendingHouseholdSwitchRef.current = null;
    }
  }, [user, authUser, isImpersonating, households, consultantClientContext]);

  const handleOpenClientHousehold = useCallback(async (context: ConsultantClientContext) => {
    if (!user) return;
    setConsultantClientContext(context);
    setActiveTab('plan');

    const updates: Partial<User> = { activeHouseholdId: context.householdId };
    pendingHouseholdSwitchRef.current = context.householdId;
    setActiveHouseholdId(context.householdId);
    const nextUser = { ...user, ...updates };
    setUser(nextUser);
    if (!isImpersonating && authUser) {
      setAuthUser({ ...authUser, ...updates });
    }
    try {
      await updateDoc(doc(db, 'users', user.uid), updates);
    } catch (err) {
      console.error('Failed to open client household', err);
      pendingHouseholdSwitchRef.current = null;
    }
  }, [user, authUser, isImpersonating]);

  const handleExitClientContext = useCallback(() => {
    setConsultantClientContext(null);
    if (!user) return;
    const personal = getConsultantSwitchableHouseholds(user, households).find(
      (household) => household.ownerId === user.uid,
    );
    if (personal) {
      void handleSwitchHousehold(personal.id);
    }
  }, [households, user, handleSwitchHousehold]);

  useEffect(() => {
    if (user && isGuidedUser(user) && activeTab === 'recipes') {
      setActiveTab('plan');
    }
  }, [user, activeTab]);

  useEffect(() => {
    if (!impersonatedUserId || allUsers.length === 0) return;
    const freshUser = allUsers.find((u) => u.uid === impersonatedUserId);
    if (!freshUser) return;
    setUser((prev) => {
      if (
        prev?.uid === freshUser.uid
        && prev.nickname === freshUser.nickname
        && prev.firstName === freshUser.firstName
        && prev.lastName === freshUser.lastName
        && prev.email === freshUser.email
        && prev.activeHouseholdId === freshUser.activeHouseholdId
      ) {
        return prev;
      }
      return freshUser;
    });
  }, [allUsers, impersonatedUserId]);

  useEffect(() => {
    activeCartRef.current = activeCart;
  }, [activeCart]);

  useEffect(() => {
    if (user?.language) {
      persistAppLanguage(user.language);
      setBootLang(user.language);
      document.documentElement.lang = user.language === 'he' ? 'he-IL' : 'en-US';
    }
  }, [user?.language]);

  // --- Theme Management ---
  useEffect(() => {
    // Determine theme: User preference > Local Storage > Default 'light'
    const themeToApply = user?.theme || (localStorage.getItem('mealPai_theme') as ThemeType) || 'light';

    const applyTheme = (targetTheme: ThemeType) => {
      const root = window.document.documentElement;
      let isDark = targetTheme === 'dark';
      
      if (targetTheme === 'system') {
        isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      }

      if (isDark) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
      
      // Save valid theme to localStorage for persistence across reloads/pre-auth
      if (['light', 'dark', 'system'].includes(targetTheme)) {
          localStorage.setItem('mealPai_theme', targetTheme);
      }
    };

    applyTheme(themeToApply);

    // Listen for system theme changes if set to 'system'
    if (themeToApply === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => applyTheme('system');
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [user?.theme]);

  // --- Font Size Management ---
  useEffect(() => {
    applyFontSize(getFontSizeMode(), getFontSizeMultiplier());
  }, []);

  // --- Back Button Functionality ---
  
  // Determine if any modal is open
  const anyModalOpen = useMemo(() => {
    return !!(isAddRecipeOpen || isChefAssistantOpen || recipeToView || isAddCartOpen || 
              cartToView || isActiveCartOpen || addMealData || isUserManagementOpen || 
              isHouseholdManagementOpen || isSubscriptionManagementOpen || isSettingsOpen || 
              isPersonalDetailsOpen || isSuggestionBoxOpen || isSuggestionManagementOpen || 
              isChefManagementOpen || isChefRequestOpen || linkedChefToEdit || chefToView ||
              isContactOpen || isHowToOpen || isTosOpen || isRemindersOpen || isNotificationsOpen || 
              cookingSessionData);
  }, [
    isAddRecipeOpen, isChefAssistantOpen, recipeToView, isAddCartOpen, 
    cartToView, isActiveCartOpen, addMealData, isUserManagementOpen, 
    isHouseholdManagementOpen, isSubscriptionManagementOpen, isSettingsOpen, 
    isPersonalDetailsOpen, isSuggestionBoxOpen, isSuggestionManagementOpen, 
    isChefManagementOpen, isChefRequestOpen, linkedChefToEdit, chefToView,
    isContactOpen, isHowToOpen, isTosOpen, isRemindersOpen, isNotificationsOpen, 
    cookingSessionData
  ]);

  // Priority-based closer for the back button
  const closeActiveModal = useCallback(() => {
    // Nested priorities (top-most modal first)
    // Recipe editor sits on top of the recipe view
    if (isAddRecipeOpen) { setIsAddRecipeOpen(false); return true; }
    if (linkedChefToEdit) { setLinkedChefToEdit(null); return true; }
    // Chef profile can sit on top of recipe view or personal details
    if (chefToView) { setChefToView(null); return true; }
    // Recipe view can sit on top of the scheduler or cooking session
    if (plannedRecipePreview) { setPlannedRecipePreview(null); return true; }
    if (recipeToView) { setRecipeToView(null); setPreviewFromScheduler(false); return true; }
    if (addMealData) { setAddMealData(null); return true; }
    if (cookingSessionData) { setCookingSessionData(null); return true; }
    if (isChefAssistantOpen) { setIsChefAssistantOpen(false); return true; }
    if (isAddCartOpen) { setIsAddCartOpen(false); return true; }
    if (cartToView) { setCartToView(null); return true; }
    if (isActiveCartOpen) { setIsActiveCartOpen(false); return true; }
    if (isRemindersOpen) { setIsRemindersOpen(false); return true; }
    if (isNotificationsOpen) { setIsNotificationsOpen(false); return true; }
    if (isTosOpen) { setIsTosOpen(false); return true; }
    if (isHowToOpen) { setIsHowToOpen(false); return true; }
    if (isContactOpen) { setIsContactOpen(false); return true; }
    if (isChefRequestOpen) { setIsChefRequestOpen(false); return true; }
    if (isChefManagementOpen) { setIsChefManagementOpen(false); return true; }
    if (isSuggestionManagementOpen) { setIsSuggestionManagementOpen(false); return true; }
    if (isSuggestionBoxOpen) { setIsSuggestionBoxOpen(false); return true; }
    if (isPersonalDetailsOpen) { setIsPersonalDetailsOpen(false); return true; }
    if (isSettingsOpen) { setIsSettingsOpen(false); return true; }
    if (isSubscriptionManagementOpen) { setIsSubscriptionManagementOpen(false); return true; }
    if (isHouseholdManagementOpen) { setIsHouseholdManagementOpen(false); return true; }
    if (isUserManagementOpen) { setIsUserManagementOpen(false); return true; }
    
    return false;
  }, [
    addMealData, cookingSessionData, recipeToView, isAddRecipeOpen, 
    isChefAssistantOpen, isAddCartOpen, cartToView, isActiveCartOpen, 
    isRemindersOpen, isNotificationsOpen, isTosOpen, isHowToOpen, isContactOpen, 
    isSuggestionManagementOpen, isSuggestionBoxOpen, isPersonalDetailsOpen, 
    isChefManagementOpen, isChefRequestOpen, linkedChefToEdit, chefToView,
    isSettingsOpen, isSubscriptionManagementOpen, isHouseholdManagementOpen, 
    isUserManagementOpen
  ]);

  // Synchronize history state with modal state
  const prevAnyModalOpen = useRef(false);
  useEffect(() => {
    if (anyModalOpen && !prevAnyModalOpen.current) {
      window.history.pushState({ isModal: true }, '');
    }
    prevAnyModalOpen.current = anyModalOpen;
  }, [anyModalOpen]);

  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      // If we are navigating back and a modal is open, close it
      if (anyModalOpen) {
        closeActiveModal();
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [anyModalOpen, closeActiveModal]);

  // Helper for manual closing: ensure we pop history if closing via UI button
  const handleCloseModal = (closeFn: () => void) => {
    closeFn();
    if (anyModalOpen) {
      window.history.back();
    }
  };

  // --- Authentication and Listeners ---

  useEffect(() => {
    let userUnsubscribe: (() => void) | null = null;
    let provisioning = false;

    getRedirectResult(auth).catch((err) => {
      console.error('Google redirect sign-in failed', err);
      setAuthInitError('Google sign-in could not be completed. Please try again.');
      setAuthLoading(false);
    });

    const authUnsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setAuthInitError(null);
        userUnsubscribe = onSnapshot(
          doc(db, 'users', firebaseUser.uid),
          async (docSnap) => {
            if (docSnap.exists()) {
              const userData = docSnap.data() as User;
              if (userData.language) {
                persistAppLanguage(userData.language);
                setBootLang(userData.language);
              }
              try {
                const { user: syncedUser, activeHouseholdId: resolvedActiveHouseholdId } = await ensurePersonalHousehold(userData);
                const pendingHouseholdId = pendingHouseholdSwitchRef.current;
                if (pendingHouseholdId && syncedUser.activeHouseholdId === pendingHouseholdId) {
                  pendingHouseholdSwitchRef.current = null;
                }
                const activeIdToApply = pendingHouseholdId && syncedUser.activeHouseholdId !== pendingHouseholdId
                  ? pendingHouseholdId
                  : resolvedActiveHouseholdId;
                setAuthUser(syncedUser);
                if (!isImpersonatingRef.current) {
                  if (pendingHouseholdId && syncedUser.activeHouseholdId !== pendingHouseholdId) {
                    setUser({ ...syncedUser, activeHouseholdId: pendingHouseholdId });
                  } else {
                    setUser(syncedUser);
                  }
                  setActiveHouseholdId(activeIdToApply);
                }
                if (syncedUser.accountMode === 'guided' && syncedUser.guidedClientId) {
                  const session = readGuidedSession();
                  if (!session || session.email !== syncedUser.email.toLowerCase()) {
                    writeGuidedSession({
                      email: syncedUser.email.toLowerCase(),
                      clientId: syncedUser.guidedClientId,
                      consultantId: session?.consultantId || '',
                      householdId: activeIdToApply,
                    });
                  }
                }
              } catch (err) {
                console.error('Failed to ensure personal household', err);
                setAuthUser(userData);
                if (!isImpersonatingRef.current) {
                  setUser(userData);
                  setActiveHouseholdId(resolveActiveHouseholdId(userData));
                }
              }

              setAuthLoading(false);
            } else if (!provisioning) {
              provisioning = true;
              try {
                const newUser = await provisionNewUser(firebaseUser);
                const lang = newUser.language || getStartupLanguage();
                persistAppLanguage(lang);
                setBootLang(lang);
                setAuthUser(newUser);
                setUser(newUser);
                setImpersonatedUserId(null);
                setActiveHouseholdId(newUser.activeHouseholdId!);
                setAuthLoading(false);
              } catch (err) {
                console.error('User provisioning failed', err);
                setAuthInitError('Your account could not be set up. Please check your connection and try again.');
                setAuthLoading(false);
              } finally {
                provisioning = false;
              }
            }
          },
          (err) => {
            console.error('User profile listener failed', err);
            setAuthInitError('Could not load your profile. Please refresh and try again.');
            setAuthLoading(false);
          }
        );
      } else {
        if (userUnsubscribe) {
          userUnsubscribe();
          userUnsubscribe = null;
        }
        setAuthUser(null);
        setUser(null);
        setImpersonatedUserId(null);
        setActiveHouseholdId('');
        setAuthLoading(false);
      }
    });

    return () => {
      authUnsubscribe();
      if (userUnsubscribe) userUnsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      setChefs([]);
      return;
    }

    const unsubChefs = onSnapshot(
      collection(db, 'chefs'),
      (snap) => {
        setChefs(snap.docs.map((d) => ({ ...d.data(), id: d.id } as Chef)));
      },
      (error) => {
        console.error('Failed to subscribe to chefs:', error);
        setChefs([]);
      },
    );

    const qChefRequests = query(collection(db, 'chefRequests'), orderBy('createdAt', 'desc'));
    const unsubChefRequests = onSnapshot(qChefRequests, (snap) => {
        setChefRequests(snap.docs.map((d) => ({ ...d.data(), id: d.id } as ChefRequest)));
    });

    return () => {
        unsubChefs();
        unsubChefRequests();
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) {
      setUserMealPreferences(null);
      return;
    }

    const unsubPrefs = onSnapshot(
      doc(db, 'userMealPreferences', user.uid),
      (snap) => {
        if (snap.exists()) {
          setUserMealPreferences(snap.data() as UserMealPreferences);
        } else {
          setUserMealPreferences(null);
        }
      },
    );

    return () => unsubPrefs();
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid || !activeHouseholdId) {
      setRecipes([]);
      setMealPlan({});
      setShoppingCarts([]);
      setActiveCart(null);
      return;
    }

    const scope = ++householdDataScopeRef.current;
    const scopedHouseholdId = activeHouseholdId;

    setRecipes([]);
    setMealPlan({});
    setShoppingCarts([]);
    setActiveCart(null);

    const qRecipes = query(
        collection(db, 'recipes'), 
        or(
            where('householdId', '==', scopedHouseholdId),
            where('isPublic', '==', true),
            where('sharedWithHouseholds', 'array-contains', scopedHouseholdId)
        )
    );
    const unsubRecipes = onSnapshot(qRecipes, (snap: any) => {
        if (householdDataScopeRef.current !== scope) return;
        const list = snap.docs
            .map((d: any) => ({ ...d.data(), id: d.id } as Recipe))
            .filter((recipe) => {
                if (recipe.isTemporary !== true || recipe.isPublic !== true) return true;
                const sharedHere = recipe.sharedWithHouseholds?.includes(scopedHouseholdId) === true;
                return recipe.householdId === scopedHouseholdId || sharedHere;
            });
        setRecipes(list);
    });

    const calculateWeekKey = (offset: number) => {
       const now = new Date();
       const startDayName = user.weekStartDay || 'Monday';
       const startDayIndex = startDayName === 'Sunday' ? 0 : 1;
       const currentDayIndex = now.getDay();
       let diff = currentDayIndex - startDayIndex;
       if (diff < 0) diff += 7;
       const weekStart = new Date(now);
       weekStart.setDate(now.getDate() - diff + (offset * 7));
       const y = weekStart.getFullYear();
       const m = String(weekStart.getMonth() + 1).padStart(2, '0');
       const d = String(weekStart.getDate()).padStart(2, '0');
       return `${y}-${m}-${d}`;
    };

    const weekKey = calculateWeekKey(weekOffset);
    const qPlan = doc(db, 'householdPlans', `${scopedHouseholdId}_${weekKey}`);
    const unsubPlan = onSnapshot(qPlan, (snap: any) => {
        if (householdDataScopeRef.current !== scope) return;
        if (snap.exists()) setMealPlan(snap.data() as MealPlan);
        else setMealPlan({});
    });

    const qCarts = query(collection(db, 'shoppingCarts'), where('householdId', '==', scopedHouseholdId));
    const unsubCarts = onSnapshot(qCarts, (snap: any) => {
        if (householdDataScopeRef.current !== scope) return;
        setShoppingCarts(snap.docs.map((d: any) => ({ ...d.data(), id: d.id } as ShoppingCart)));
    });

    const qActiveCart = doc(db, 'activeShoppingCarts', scopedHouseholdId);
    const unsubActiveCart = onSnapshot(qActiveCart, (snap: any) => {
        if (householdDataScopeRef.current !== scope) return;
        const nextCart: ActiveShoppingCart = snap.exists()
            ? {
                ...(snap.data() as ActiveShoppingCart),
                householdId: scopedHouseholdId,
                items: Array.isArray((snap.data() as ActiveShoppingCart).items)
                    ? (snap.data() as ActiveShoppingCart).items
                    : [],
            }
            : {
                householdId: scopedHouseholdId,
                items: [],
                updatedAt: new Date().toISOString(),
            };

        setActiveCart((prev) => {
            if (activeCartSnapshotKey(prev) === activeCartSnapshotKey(nextCart)) {
                return prev;
            }
            return nextCart;
        });
    });

    const qHouseholds = query(collection(db, 'households'), where('members', 'array-contains', user.uid));
    const unsubHouseholds = onSnapshot(qHouseholds, (snap: any) => {
        setHouseholds(snap.docs.map((d: any) => ({ ...d.data(), id: d.id } as Household)));
    });

    const qInvited = query(collection(db, 'households'), where('invitedMembers', 'array-contains', user.uid));
    const unsubInvited = onSnapshot(qInvited, (snap: any) => {
        setInvitedHouseholds(snap.docs.map((d: any) => ({ ...d.data(), id: d.id } as Household)));
    });

    const qSuggestions = query(collection(db, 'suggestions'), orderBy('date', 'desc'));
    const unsubSuggestions = onSnapshot(qSuggestions, (snap: any) => {
        setSuggestions(snap.docs.map((d: any) => ({ ...d.data(), id: d.id } as Suggestion)));
    });

    const qOverrides = query(collection(db, 'item_category_overrides'));
    const unsubOverrides = onSnapshot(qOverrides, (snap: any) => {
        const overrides: Record<string, string> = {};
        snap.forEach((doc: any) => {
            overrides[doc.id] = doc.data().category;
        });
        setCategoryOverrides(overrides);
    });

    let unsubUsers = () => {};
    if (authUser && (authUser.role === 'admin' || authUser.role === 'superuser')) {
        const qUsers = query(collection(db, 'users'));
        unsubUsers = onSnapshot(qUsers, (snap: any) => {
            setAllUsers(snap.docs.map((d: any) => ({ ...d.data(), uid: d.id } as User)));
        });
    } else {
        setAllUsers([]);
    }

    return () => {
        unsubRecipes();
        unsubPlan();
        unsubCarts();
        unsubActiveCart();
        unsubHouseholds();
        unsubInvited();
        unsubSuggestions();
        unsubOverrides();
        unsubUsers();
    };
  }, [user?.uid, user?.weekStartDay, authUser, activeHouseholdId, weekOffset]);

  useEffect(() => {
    if (!user || !activeHouseholdId) {
      setReminderMealPlans({});
      return;
    }

    const scope = householdDataScopeRef.current;
    setReminderMealPlans({});

    const weekStartDay = user.weekStartDay || 'Monday';
    const weekKeys = [0, 1].map((offset) => getWeekKey(offset, weekStartDay));
    const unsubs = weekKeys.map((weekKey) => {
      const docId = `${activeHouseholdId}_${weekKey}`;
      return onSnapshot(doc(db, 'householdPlans', docId), (snap) => {
        if (householdDataScopeRef.current !== scope) return;
        setReminderMealPlans((prev) => {
          const next = { ...prev };
          if (snap.exists()) {
            next[weekKey] = snap.data() as MealPlan;
          } else {
            delete next[weekKey];
          }
          return next;
        });
      });
    });

    return () => {
      unsubs.forEach((unsub) => unsub());
    };
  }, [user, activeHouseholdId]);

  useEffect(() => {
    if (!user || households.length === 0) return;
    if (pendingHouseholdSwitchRef.current) return;

    const householdIds = [...new Set([
      ...households.map((household) => household.id),
      ...(user.households || []),
    ])];

    const resolved = canAccessGuidedClientsAdmin(user)
      ? resolveConsultantActiveHouseholdId(user, households, consultantClientContext?.householdId)
      : resolveActiveHouseholdId(user, householdIds);

    setActiveHouseholdId((current) => {
      if (current === resolved) return current;
      if (current && user.activeHouseholdId === current && householdIds.includes(current)) {
        return current;
      }
      return resolved;
    });

    const activeIsValid = !!user.activeHouseholdId && householdIds.includes(user.activeHouseholdId);
    if (!activeIsValid && user.activeHouseholdId !== resolved) {
      updateDoc(doc(db, 'users', user.uid), { activeHouseholdId: resolved }).catch((err) => {
        console.error('Failed to sync active household', err);
      });
    }
  }, [user, households, consultantClientContext?.householdId]);

  const handleUpdateCategoryKnowledge = async (itemName: string, newCategory: string) => {
      if (!user) return;
      const normalizedName = itemName.toLowerCase().trim();
      const listPillars = buildListPillars(shoppingCarts);
      const categoryValue = normalizeCategoryAssignment(newCategory, listPillars);

      await setDoc(doc(db, 'item_category_overrides', normalizedName), {
          category: categoryValue,
          updatedBy: user.uid,
          updatedAt: new Date().toISOString()
      });

      if (activeCart) {
          await handleUpdateActiveCart((latest) => {
              const items = latest.items ?? [];
              const nextItems = items.map((item) =>
                  item.name.toLowerCase().trim() === normalizedName
                      ? { ...item, category: categoryValue }
                      : item,
              );
              if (!nextItems.some((item, idx) => item !== items[idx])) {
                  return latest;
              }
              return { ...latest, items: nextItems };
          });
      }
  };

  const activeHousehold = households.find(h => h.id === activeHouseholdId);
  const householdRole = activeHousehold?.memberRoles?.[user?.uid || ''] || (activeHousehold?.ownerId === user?.uid ? 'owner' : 'viewer');
  const orderedDays = user?.weekStartDay === 'Sunday' 
    ? ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const defrostReminders = useMemo(() => {
    if (!user) return [];
    return buildDefrostReminders({
      mealPlans: reminderMealPlans,
      recipes,
      orderedDays,
      language: user.language,
    });
  }, [reminderMealPlans, recipes, orderedDays, user]);

  const calculateCurrentWeekKey = (offset: number) => {
      const now = new Date();
      const startDayName = user?.weekStartDay || 'Monday';
      const startDayIndex = startDayName === 'Sunday' ? 0 : 1;
      const currentDayIndex = now.getDay();
      let diff = currentDayIndex - startDayIndex;
      if (diff < 0) diff += 7;
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - diff + (offset * 7));
      const y = weekStart.getFullYear();
      const m = String(weekStart.getMonth() + 1).padStart(2, '0');
      const d = String(weekStart.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
  };

  const handleCreateHousehold = async (name: string, visibleMeals?: MealType[], adults?: number, children?: number) => {
      if (!user) return;
      const ownedCount = households.filter(
        (household) => household.ownerId === user.uid && !isGuidedClientHousehold(household),
      ).length;
      if (!canUserCreateHousehold(user, ownedCount)) {
          const limit = getUserHouseholdLimit(user);
          alert(getTranslation(user.language || 'en', 'householdLimitDesc').replace('{limit}', limit.toString()));
          return;
      }
      try {
          const newHouseholdRef = doc(collection(db, 'households'));
          const residentsPatch = buildResidentsUpdate(adults ?? 1, children ?? 0);
          const newHousehold: Household = {
              id: newHouseholdRef.id,
              name,
              ownerId: user.uid,
              members: [user.uid],
              memberRoles: { [user.uid]: 'owner' },
              ...residentsPatch,
              visibleMealTypes: visibleMeals || ['Breakfast', 'Lunch', 'Dinner']
          };
          await setDoc(newHouseholdRef, newHousehold);
          
          const updatedHouseholds = [...(user.households || []), newHouseholdRef.id];
          await updateDoc(doc(db, 'users', user.uid), {
              households: updatedHouseholds,
              activeHouseholdId: newHouseholdRef.id
          });
          setActiveHouseholdId(newHouseholdRef.id);
      } catch (error) {
          console.error("Error creating household", error);
          alert("Error creating household");
      }
  };

  const handleAddMember = async (householdId: string, email: string) => {
      try {
          const q = query(collection(db, 'users'), where('email', '==', email.toLowerCase().trim()));
          const querySnapshot = await getDocs(q);
          if (querySnapshot.empty) {
              setGenericNotice({ title: t('userNotFound'), variant: 'error' });
              return;
          }
          const targetUser = querySnapshot.docs[0];
          const targetUserData = targetUser.data() as User;
          
          if (targetUserData.allowHouseholdInvitations !== true) {
              setGenericNotice({ title: t('userNotFound'), variant: 'error' });
              return;
          }
          
          const targetUserId = targetUser.id;
          
          const household = households.find(h => h.id === householdId);
          if (!household) return;
          
          if (household.members.includes(targetUserId)) {
              setGenericNotice({ title: t('userAlreadyMember'), variant: 'error' });
              return;
          }
          
          if (household.invitedMembers?.includes(targetUserId)) {
              setGenericNotice({ title: t('userAlreadyInvited'), variant: 'error' });
              return;
          }
          
          const updatedInvited = [...(household.invitedMembers || []), targetUserId];
          const updatedRejected = { ...(household.rejectedMembers || {}) };
          delete updatedRejected[targetUserId];

          await updateDoc(doc(db, 'households', householdId), {
              invitedMembers: updatedInvited,
              rejectedMembers: updatedRejected
          });
          
          setGenericNotice({ title: t('invitationSent'), variant: 'success' });
      } catch (error) {
          console.error("Error sending invitation", error);
          setGenericNotice({ title: t('errorSendingInvitation'), variant: 'error' });
      }
  };

  const handleAcceptInvitation = async (householdId: string) => {
      if (!user) return;
      try {
          const householdRef = doc(db, 'households', householdId);
          const householdSnap = await getDoc(householdRef);
          if (!householdSnap.exists()) return;
          const household = householdSnap.data() as Household;
          
          const updatedInvited = (household.invitedMembers || []).filter(uid => uid !== user.uid);
          const updatedMembers = [...(household.members || []), user.uid];
          const updatedRoles = { ...(household.memberRoles || {}), [user.uid]: 'viewer' as HouseholdRole };
          
          await updateDoc(householdRef, {
              invitedMembers: updatedInvited,
              members: updatedMembers,
              memberRoles: updatedRoles
          });
          
          const userRef = doc(db, 'users', user.uid);
          const updatedUserHouseholds = [...(user.households || []), householdId];
          await updateDoc(userRef, {
              households: updatedUserHouseholds,
              activeHouseholdId: householdId
          });
          setActiveHouseholdId(householdId);
      } catch (error) {
          console.error("Error accepting invitation", error);
      }
  };

  const handleRejectInvitation = async (householdId: string) => {
      if (!user) return;
      try {
          const householdRef = doc(db, 'households', householdId);
          const householdSnap = await getDoc(householdRef);
          if (!householdSnap.exists()) return;
          const household = householdSnap.data() as Household;
          
          const updatedInvited = (household.invitedMembers || []).filter(uid => uid !== user.uid);
          const updatedRejected = { ...(household.rejectedMembers || {}), [user.uid]: Date.now() };
          
          await updateDoc(householdRef, {
              invitedMembers: updatedInvited,
              rejectedMembers: updatedRejected
          });
      } catch (error) {
          console.error("Error rejecting invitation", error);
      }
  };

  const handleRemoveMember = async (householdId: string, userId: string) => {
      try {
          const household = households.find(h => h.id === householdId);
          if (!household) return;
          
          const updatedMembers = household.members.filter(id => id !== userId);
          const updatedRoles = { ...(household.memberRoles || {}) };
          delete updatedRoles[userId];
          
          await updateDoc(doc(db, 'households', householdId), {
              members: updatedMembers,
              memberRoles: updatedRoles
          });
          
          const targetUserDoc = await getDoc(doc(db, 'users', userId));
          if (targetUserDoc.exists()) {
              const targetUserData = targetUserDoc.data() as User;
              const updatedUserHouseholds = (targetUserData.households || []).filter(id => id !== householdId);
              const updates: any = { households: updatedUserHouseholds };
              if (targetUserData.activeHouseholdId === householdId) {
                  const personalHouseholdId = getPersonalHouseholdId(userId);
                  updates.activeHouseholdId = updatedUserHouseholds.includes(personalHouseholdId)
                      ? personalHouseholdId
                      : (updatedUserHouseholds.length > 0 ? updatedUserHouseholds[0] : null);
              }
              await updateDoc(doc(db, 'users', userId), updates);
          }
      } catch (error) {
          console.error("Error removing member", error);
      }
  };

  const handleUpdateMemberRole = async (householdId: string, userId: string, role: HouseholdRole) => {
      try {
          const household = households.find(h => h.id === householdId);
          if (!household) return;
          const updatedRoles = { ...(household.memberRoles || {}), [userId]: role };
          await updateDoc(doc(db, 'households', householdId), {
              memberRoles: updatedRoles
          });
      } catch (error) {
          console.error("Error updating role", error);
      }
  };

  const handleDeleteHousehold = async (householdId: string) => {
      if (!user) return;
      try {
          const household = households.find(h => h.id === householdId);
          if (!household) return;
          
          for (const memberId of household.members) {
              const targetUserDoc = await getDoc(doc(db, 'users', memberId));
              if (targetUserDoc.exists()) {
                  const targetUserData = targetUserDoc.data() as User;
                  const updatedUserHouseholds = (targetUserData.households || []).filter(id => id !== householdId);
                  const updates: any = { households: updatedUserHouseholds };
                  if (targetUserData.activeHouseholdId === householdId) {
                      const personalHouseholdId = getPersonalHouseholdId(memberId);
                      updates.activeHouseholdId = updatedUserHouseholds.includes(personalHouseholdId)
                          ? personalHouseholdId
                          : (updatedUserHouseholds.length > 0 ? updatedUserHouseholds[0] : null);
                  }
                  await updateDoc(doc(db, 'users', memberId), updates);
              }
          }
          
          await deleteDoc(doc(db, 'households', householdId));
          if (activeHouseholdId === householdId) {
              const remaining = households.filter(h => h.id !== householdId);
              const personalHouseholdId = getPersonalHouseholdId(user.uid);
              const nextActive = remaining.find(h => h.id === personalHouseholdId)?.id || remaining[0]?.id || '';
              if (nextActive) {
                  setActiveHouseholdId(nextActive);
                  await updateDoc(doc(db, 'users', user.uid), { activeHouseholdId: nextActive });
              } else {
                  setActiveHouseholdId('');
              }
          }
      } catch (error) {
          console.error("Error deleting household", error);
      }
  };

  const handleToggleFavoriteRecipe = useCallback(async (recipeId: string) => {
    if (!user) return;
    await toggleFavoriteRecipe(user.uid, recipeId, user);
  }, [user]);

  const handleToggleFavoriteChef = useCallback(async (chefId: string) => {
    if (!user) return;
    await toggleFavoriteChef(user.uid, chefId, user);
  }, [user]);

  const handleUpdateActiveCart: UpdateActiveShoppingCartFn = async (cartOrUpdater) => {
      if (!activeHouseholdId) return;

      const currentCart: ActiveShoppingCart = activeCartRef.current ?? {
          householdId: activeHouseholdId,
          items: [],
          updatedAt: new Date().toISOString(),
      };

      const optimisticNext = typeof cartOrUpdater === 'function'
          ? cartOrUpdater(currentCart)
          : {
              ...currentCart,
              ...(cartOrUpdater.collectionOrder !== undefined
                  ? { collectionOrder: cartOrUpdater.collectionOrder }
                  : {}),
              ...(cartOrUpdater.items !== undefined ? { items: cartOrUpdater.items } : {}),
          };

      const optimisticCart: ActiveShoppingCart = {
          ...optimisticNext,
          householdId: activeHouseholdId,
          items: optimisticNext.items ?? [],
      };

      setActiveCart((prev) => {
          if (activeCartSnapshotKey(prev) === activeCartSnapshotKey(optimisticCart)) {
              return prev;
          }
          return optimisticCart;
      });

      const ref = doc(db, 'activeShoppingCarts', activeHouseholdId);
      const snap = await getDoc(ref);
      const latest: ActiveShoppingCart = snap.exists()
          ? {
              ...(snap.data() as ActiveShoppingCart),
              householdId: activeHouseholdId,
              items: Array.isArray((snap.data() as ActiveShoppingCart).items)
                  ? (snap.data() as ActiveShoppingCart).items
                  : [],
          }
          : { householdId: activeHouseholdId, items: [], updatedAt: new Date().toISOString() };

      const next = typeof cartOrUpdater === 'function'
          ? cartOrUpdater(latest)
          : {
              ...latest,
              ...(cartOrUpdater.collectionOrder !== undefined
                  ? { collectionOrder: cartOrUpdater.collectionOrder }
                  : {}),
              ...(cartOrUpdater.items !== undefined ? { items: cartOrUpdater.items } : {}),
          };

      const latestItemCount = latest.items?.length ?? 0;
      const nextItemCount = next.items?.length ?? 0;
      if (
          typeof cartOrUpdater !== 'function'
          && cartOrUpdater.items !== undefined
          && nextItemCount === 0
          && latestItemCount > 0
      ) {
          console.warn('Blocked stale active cart write that would clear items');
          return;
      }

      const payload = {
          items: next.items ?? [],
          ...(next.collectionOrder !== undefined ? { collectionOrder: next.collectionOrder } : {}),
          householdId: activeHouseholdId,
          updatedAt: new Date().toISOString(),
      };

      if (snap.exists()) {
          await updateDoc(ref, payload);
      } else {
          await setDoc(ref, payload);
      }
  };

  const handleSaveActiveCartExpandedSections = useCallback(async (expandedSections: string[]) => {
      if (!user?.uid || !activeHouseholdId) return;

      const existing = user.activeCartExpandedSections?.[activeHouseholdId];
      const nextKey = expandedSections.join('|');
      if (existing && existing.join('|') === nextKey) {
          return;
      }

      const nextExpandedByHousehold = {
          ...(user.activeCartExpandedSections ?? {}),
          [activeHouseholdId]: expandedSections,
      };

      await updateDoc(doc(db, 'users', user.uid), {
          activeCartExpandedSections: nextExpandedByHousehold,
      });
  }, [user?.uid, user?.activeCartExpandedSections, activeHouseholdId]);

  const activeCartSavedExpandedSections = useMemo(() => {
      if (!user?.activeCartExpandedSections || !activeHouseholdId) return undefined;
      return user.activeCartExpandedSections[activeHouseholdId];
  }, [user?.activeCartExpandedSections, activeHouseholdId]);

  const hasActiveCartSavedSectionState = Boolean(
      user?.activeCartExpandedSections
      && activeHouseholdId
      && Object.prototype.hasOwnProperty.call(user.activeCartExpandedSections, activeHouseholdId),
  );

  const addIngredientsToActiveCart = async (
      ingredients: { 
          name: string, 
          quantity: number, 
          unit: string, 
          isMeat: boolean,
          sources?: { originName: string; originType: 'recipe' | 'manual' | 'list' | 'day'; originId?: string; quantity?: number }[]
      }[], 
      originName: string, 
      originType: 'recipe' | 'manual' | 'list' | 'day' = 'recipe'
  ) => {
      if (!activeCart) return;

      await handleUpdateActiveCart((latest) => {
          const nextItems = [...(latest.items ?? [])];
          ingredients.forEach(ing => {
              const nameClean = ing.name.trim();
              if (!nameClean) return;
              const itemId = `${nameClean.toLowerCase()}_${ing.unit.toLowerCase().trim()}`;
              const existingIndex = nextItems.findIndex(i => i.id === itemId);
              
              const newSources = ing.sources && ing.sources.length > 0
                  ? ing.sources.map((src, idx) => ({
                      id: `${Date.now()}-${idx}-${Math.random().toString(36).substr(2, 5)}`,
                      originName: src.originName,
                      quantity: src.quantity ?? ing.quantity,
                      originType: src.originType,
                      originId: src.originId
                  }))
                  : [{
                      id: `${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
                      originName: originName,
                      quantity: ing.quantity,
                      originType: originType
                  }];

              if (existingIndex > -1) {
                  nextItems[existingIndex] = { 
                      ...nextItems[existingIndex], 
                      done: false, 
                      sources: [...nextItems[existingIndex].sources, ...newSources] 
                  };
              } else {
                  nextItems.push({
                      id: itemId,
                      name: nameClean,
                      unit: ing.unit,
                      isMeat: ing.isMeat,
                      done: false,
                      category: categoryOverrides[nameClean.toLowerCase()] || detectCategory(ing.name, ing.isMeat),
                      sources: newSources
                  });
              }
          });
          return { ...latest, items: nextItems };
      });
  };

  const handleAddRecipeToDay = async (recipeIds: string[], day: DayOfWeek, mealType: MealType, targetWeekOffset: number = weekOffset, customServings?: number) => {
      const weekKey = calculateCurrentWeekKey(targetWeekOffset);
      const docId = `${activeHouseholdId}_${weekKey}`;
      const planDoc = await getDoc(doc(db, 'householdPlans', docId));
      let currentPlan: MealPlan = planDoc.exists() ? planDoc.data() as MealPlan : { 
          householdId: activeHouseholdId, 
          weekStartDate: weekKey,
          creationDate: new Date().toISOString()
      };
      const existingMeals = currentPlan[day] || [];

      // Check duplicates
      for (const rid of recipeIds) {
          const duplicate = existingMeals.find(m => !m.isDeleted && m.recipeId === rid && m.mealType === mealType);
          if (duplicate) {
              const r = recipes.find(x => x.id === rid);
              setScheduledConflict({
                  recipeName: r?.name || duplicate.recipeName || 'Recipe',
                  day,
                  mealType
              });
              throw new Error('DUPLICATE_MEAL');
          }
      }

      const newMeals: PlannedMeal[] = recipeIds.map(rid => {
          const r = recipes.find(x => x.id === rid);
          return { id: Math.random().toString(36).substr(2, 9), recipeId: rid, plannedServings: customServings || r?.servings || 1, mealType, recipeName: r?.name, sourcingStatus: 'buy' };
      });
      await setDoc(doc(db, 'householdPlans', docId), { ...currentPlan, [day]: [...existingMeals, ...newMeals] });
      await touchHouseholdPlanMeta(activeHouseholdId, user.uid);
  };

  const handleRemoveRecipeFromDay = async (pmId: string, day: DayOfWeek, targetWeekOffset: number = weekOffset) => {
      const weekKey = calculateCurrentWeekKey(targetWeekOffset);
      const docId = `${activeHouseholdId}_${weekKey}`;
      const planDoc = await getDoc(doc(db, 'householdPlans', docId));
      if (planDoc.exists()) {
          const currentPlan = planDoc.data() as MealPlan;
          const dayMeals = currentPlan[day] || [];
          const removedMeal = dayMeals.find(m => m.id === pmId || m.recipeId === pmId);
          const updatedMeals = dayMeals.filter(m => m.id !== pmId && m.recipeId !== pmId);
          await updateDoc(doc(db, 'householdPlans', docId), { [day]: updatedMeals });
          await touchHouseholdPlanMeta(activeHouseholdId, user.uid);

          if (removedMeal && activeHouseholdId) {
              const mealType = removedMeal.mealType || 'Dinner';
              const remainingInSlot = updatedMeals.filter((meal) => mealBelongsToSlot(meal, mealType));
              if (remainingInSlot.length === 0) {
                  const sessionKey = buildCookingSessionDocId(targetWeekOffset, day, mealType);
                  await deleteDoc(doc(db, 'households', activeHouseholdId, 'cookingSessions', sessionKey)).catch(() => undefined);
              }
          }
      }
  };

  const handleUpdateMeal = async (
      pmId: string,
      day: DayOfWeek,
      updates: { servings?: number; status?: 'buy' | 'pantry' | 'prepared'; mealType?: MealType },
      targetWeekOffset: number = weekOffset,
  ) => {
      const weekKey = calculateCurrentWeekKey(targetWeekOffset);
      const docId = `${activeHouseholdId}_${weekKey}`;
      const planDoc = await getDoc(doc(db, 'householdPlans', docId));
      if (planDoc.exists()) {
          const currentPlan = planDoc.data() as MealPlan;
          const dayMeals = currentPlan[day] || [];

          if (updates.mealType !== undefined) {
              const currentMeal = dayMeals.find(m => m.id === pmId || m.recipeId === pmId);
              if (currentMeal) {
                  const isDuplicate = dayMeals.some(m => !m.isDeleted && m.id !== pmId && m.recipeId === currentMeal.recipeId && m.mealType === updates.mealType);
                  if (isDuplicate) {
                      const r = recipes.find(x => x.id === currentMeal.recipeId);
                      setScheduledConflict({
                          recipeName: r?.name || currentMeal.recipeName || 'Recipe',
                          day,
                          mealType: updates.mealType
                      });
                      throw new Error('DUPLICATE_MEAL');
                  }
              }
          }

          const updatedMeals = dayMeals.map(m => {
              if (m.id !== pmId && m.recipeId !== pmId) return m;
              return {
                  ...m,
                  plannedServings: updates.servings !== undefined ? updates.servings : m.plannedServings,
                  sourcingStatus: updates.status !== undefined ? updates.status : m.sourcingStatus,
                  mealType: updates.mealType !== undefined ? updates.mealType : m.mealType,
              };
          });
          await updateDoc(doc(db, 'householdPlans', docId), { [day]: updatedMeals });
          await touchHouseholdPlanMeta(activeHouseholdId, user.uid);
      }
  };

  const handleUpdateMealSlotTitle = async (
      day: DayOfWeek,
      mealType: MealType,
      title: string | null,
      targetWeekOffset: number = weekOffset,
  ) => {
      const weekKey = calculateCurrentWeekKey(targetWeekOffset);
      const docId = `${activeHouseholdId}_${weekKey}`;
      const planDoc = await getDoc(doc(db, 'householdPlans', docId));
      if (!planDoc.exists()) return;

      const currentPlan = planDoc.data() as MealPlan;
      const slotKey = buildMealSlotTitleKey(day, mealType);
      const slotMenuTitles = { ...(currentPlan.slotMenuTitles ?? {}) };
      const trimmedTitle = title?.trim();

      if (trimmedTitle) {
          slotMenuTitles[slotKey] = trimmedTitle;
      } else {
          delete slotMenuTitles[slotKey];
      }

      await updateDoc(doc(db, 'householdPlans', docId), { slotMenuTitles });
      await touchHouseholdPlanMeta(activeHouseholdId, user.uid);

      if (targetWeekOffset === weekOffset) {
          setMealPlan((previousPlan) => ({ ...previousPlan, slotMenuTitles }));
      }

      setCookingSessionData((previousSession) => {
          if (
              !previousSession
              || previousSession.day !== day
              || previousSession.type !== mealType
              || previousSession.weekOffset !== targetWeekOffset
          ) {
              return previousSession;
          }

          return { ...previousSession, slotMenuTitles };
      });
  };

  const handleMoveMeal = async (pmId: string, fromDay: DayOfWeek, toDay: DayOfWeek, toType: MealType, targetWeekOffset: number = weekOffset) => {
      const weekKey = calculateCurrentWeekKey(targetWeekOffset);
      const docId = `${activeHouseholdId}_${weekKey}`;
      const planDoc = await getDoc(doc(db, 'householdPlans', docId));
      
      if (planDoc.exists()) {
          const currentPlan = planDoc.data() as MealPlan;
          const fromMeals = currentPlan[fromDay] || [];
          const mealToMove = fromMeals.find(m => m.id === pmId || m.recipeId === pmId);
          if (!mealToMove) return;

          if (fromDay === toDay) {
              const isDuplicate = fromMeals.some(m => !m.isDeleted && m.id !== pmId && m.recipeId === mealToMove.recipeId && m.mealType === toType);
              if (isDuplicate) {
                  const r = recipes.find(x => x.id === mealToMove.recipeId);
                  setScheduledConflict({
                      recipeName: r?.name || mealToMove.recipeName || 'Recipe',
                      day: fromDay,
                      mealType: toType
                  });
                  throw new Error('DUPLICATE_MEAL');
              }
              const updatedMeals = fromMeals.map(m => (
                  m.id === pmId || m.recipeId === pmId
                      ? { ...m, mealType: toType }
                      : m
              ));
              await updateDoc(doc(db, 'householdPlans', docId), { [fromDay]: updatedMeals });
              await touchHouseholdPlanMeta(activeHouseholdId, user.uid);
              return;
          }

          const toMeals = currentPlan[toDay] || [];
          const isDuplicate = toMeals.some(m => !m.isDeleted && m.id !== pmId && m.recipeId === mealToMove.recipeId && m.mealType === toType);
          if (isDuplicate) {
              const r = recipes.find(x => x.id === mealToMove.recipeId);
              setScheduledConflict({
                  recipeName: r?.name || mealToMove.recipeName || 'Recipe',
                  day: toDay,
                  mealType: toType
              });
              throw new Error('DUPLICATE_MEAL');
          }

          const updatedFromMeals = fromMeals.filter(m => m.id !== pmId && m.recipeId !== pmId);
          const updatedMeal = { ...mealToMove, mealType: toType };
          
          await updateDoc(doc(db, 'householdPlans', docId), { 
              [fromDay]: updatedFromMeals,
              [toDay]: [...toMeals, updatedMeal],
          });
          await touchHouseholdPlanMeta(activeHouseholdId, user.uid);
      }
  };

  const handleReschedulePlannedMeal = async (
      pmId: string,
      fromDay: DayOfWeek,
      fromOffset: number,
      toDay: DayOfWeek,
      toType: MealType,
      toOffset: number,
      updates: { servings?: number; status?: 'buy' | 'pantry' | 'prepared' },
  ) => {
      const fromWeekKey = calculateCurrentWeekKey(fromOffset);
      const toWeekKey = calculateCurrentWeekKey(toOffset);
      const fromDocId = `${activeHouseholdId}_${fromWeekKey}`;
      const toDocId = `${activeHouseholdId}_${toWeekKey}`;

      const fromDoc = await getDoc(doc(db, 'householdPlans', fromDocId));
      if (!fromDoc.exists()) return;

      const fromPlan = fromDoc.data() as MealPlan;
      const fromMeals = fromPlan[fromDay] || [];
      const mealToMove = fromMeals.find(m => m.id === pmId);
      if (!mealToMove) return;

      const toDoc = await getDoc(doc(db, 'householdPlans', toDocId));
      const toPlan: MealPlan = toDoc.exists()
          ? toDoc.data() as MealPlan
          : {
              householdId: activeHouseholdId,
              weekStartDate: toWeekKey,
              creationDate: new Date().toISOString(),
          };

      const toMeals = toPlan[toDay] || [];
      const isDuplicate = toMeals.some(m => !m.isDeleted && m.id !== pmId && m.recipeId === mealToMove.recipeId && m.mealType === toType);
      if (isDuplicate) {
          const r = recipes.find(x => x.id === mealToMove.recipeId);
          setScheduledConflict({
              recipeName: r?.name || mealToMove.recipeName || 'Recipe',
              day: toDay,
              mealType: toType
          });
          throw new Error('DUPLICATE_MEAL');
      }

      await updateDoc(doc(db, 'householdPlans', fromDocId), {
          [fromDay]: fromMeals.filter(m => m.id !== pmId),
      });

      const rescheduledMeal: PlannedMeal = {
          ...mealToMove,
          mealType: toType,
          plannedServings: updates.servings ?? mealToMove.plannedServings,
          sourcingStatus: updates.status ?? mealToMove.sourcingStatus,
      };

      await setDoc(doc(db, 'householdPlans', toDocId), {
          ...toPlan,
          [toDay]: [...toMeals, rescheduledMeal],
      }, { merge: true });
      await touchHouseholdPlanMeta(activeHouseholdId, user.uid);
  };

  const guardTemporaryRecipe = <T extends { isTemporary?: boolean; isPublic?: boolean; sharedWithHouseholds?: string[] }>(recipe: T): T => {
      if (recipe.isTemporary !== true) return recipe;
      if (!user) return { ...recipe, isPublic: false, sharedWithHouseholds: [] };
      return constrainTemporaryRecipeVisibility(recipe, user, households);
  };

  const handleQuickCreateRecipe = async (name: string) => {
      const newRecipe = guardTemporaryRecipe({
          name,
          ingredients: [],
          instructions: [],
          servings: 2,
          links: [],
          ownerId: user?.uid || '',
          householdId: activeHouseholdId,
          isPublic: false,
          isTemporary: true,
          sharedWithHouseholds: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
      });
      const docRef = await addDoc(collection(db, 'recipes'), newRecipe);
      return docRef.id;
  };

  const unlockPendingRecipe = useCallback(async (recipeId: string) => {
      await updateDoc(doc(db, 'recipes', recipeId), {
          saveStatus: 'ready',
          updatedAt: new Date().toISOString(),
      });
  }, []);

  const runBackgroundRecipeFinalize = useCallback(async (recipe: Recipe) => {
      if (!recipe.id || pendingRecipeFinalizeRef.current.has(recipe.id)) return;
      if (!isRecipeSavePending(recipe)) return;

      pendingRecipeFinalizeRef.current.add(recipe.id);
      try {
          if (isStalePendingRecipe(recipe)) {
              await unlockPendingRecipe(recipe.id);
              return;
          }

          const encryptedKey = !user?.isAiEligible ? user?.encryptedGeminiKey : undefined;
          const finalizeInput = buildFinalizeInputFromRecipe(recipe, encryptedKey);
          if (!finalizeInput.needsNutrition && !finalizeInput.needsTranslation) {
              await unlockPendingRecipe(recipe.id);
              return;
          }

          const finalizeTimeoutMs = 120_000;
          const result = await Promise.race([
              finalizePendingRecipe(finalizeInput),
              new Promise<never>((_, reject) => {
                  window.setTimeout(
                      () => reject(new Error('Background recipe finalize timed out')),
                      finalizeTimeoutMs,
                  );
              }),
          ]);
          await updateDoc(
              doc(db, 'recipes', recipe.id),
              stripUndefinedFields({ ...result }),
          );
      } catch (error) {
          console.error('Background recipe finalize failed:', error);
          try {
              // Unlock the card even if AI enrichment fails so the user is not stuck.
              await unlockPendingRecipe(recipe.id);
          } catch (unlockError) {
              console.error('Failed to unlock pending recipe:', unlockError);
          }
      } finally {
          pendingRecipeFinalizeRef.current.delete(recipe.id);
      }
  }, [unlockPendingRecipe, user?.encryptedGeminiKey, user?.isAiEligible]);

  useEffect(() => {
      if (!user?.uid) return;
      recipes
          .filter((recipe) => isRecipeSavePending(recipe) && recipe.ownerId === user.uid)
          .forEach((recipe) => {
              void runBackgroundRecipeFinalize(recipe);
          });
  }, [recipes, user?.uid, runBackgroundRecipeFinalize]);

  useEffect(() => {
    captureShareLaunchFromLocation();
  }, []);

  const runShareImport = useCallback(async (payload: ShareLaunchPayload) => {
    const activeUser = user;
    if (!activeUser?.uid || !activeHouseholdId) return;
    if (shareImportRunningRef.current) return;

    const url = extractShareUrl(payload);
    if (!url) {
      clearPendingShareFromStorage();
      void logShareImportEvent({
        userId: activeUser.uid,
        outcome: 'no_url',
        stage: 'extract_url',
        code: 'no_url',
        messageKey: 'shareImportNoUrl',
        rawPayload: payload,
        extractedUrl: null,
        normalizedUrl: null,
        shareContextText: [payload.text, payload.title].filter(Boolean).join('\n'),
      });
      setGenericNotice({
        title: t('shareImportFailedTitle'),
        message: t('shareImportNoUrl'),
        variant: 'error',
      });
      await createUserNotification({
        userId: activeUser.uid,
        type: 'share_import_failure',
        title: t('shareImportFailedTitle'),
        body: t('shareImportNoUrl'),
      });
      return;
    }

    shareImportRunningRef.current = true;

    const startedMessage = t('shareImportStartedBody').replace('{url}', url);
    setGenericNotice({
      title: t('shareImportStartedTitle'),
      message: startedMessage,
      variant: 'info',
    });

    void createUserNotification({
      userId: activeUser.uid,
      type: 'share_import_started',
      title: t('shareImportStartedTitle'),
      body: startedMessage,
    });

    void (async () => {
      try {
        const conflict = await findConflictingDiscoverableRecipeForUrl(url, {
          knownRecipes: recipes,
        });
        if (conflict) {
          await createUserNotification({
            userId: activeUser.uid,
            type: 'share_import_duplicate',
            title: t('shareImportDuplicateTitle'),
            body: t('shareImportDuplicateBody').replace('{name}', conflict.recipe.name),
            recipeId: conflict.recipe.id,
          });
          return;
        }

        const result = await requestServerShareImport({
          url,
          userId: activeUser.uid,
          householdId: activeHouseholdId,
          targetLanguage: activeUser.language || 'en',
          userEncryptedKey: activeUser.encryptedGeminiKey,
          payload,
        });

        if (result.status === 'saved') {
          void logShareImportEvent({
            userId: activeUser.uid,
            outcome: 'success',
            stage: 'server_saved',
            code: 'saved',
            rawPayload: payload,
            extractedUrl: url,
            normalizedUrl: url,
            shareContextText: [payload.text, payload.title].filter(Boolean).join('\n'),
          });
          return;
        }

        if (result.status === 'failed') {
          void logShareImportEvent({
            userId: activeUser.uid,
            outcome: 'failure',
            stage: 'server_failed',
            code: result.code,
            messageKey: result.messageKey,
            rawPayload: payload,
            extractedUrl: url,
            normalizedUrl: url,
            shareContextText: [payload.text, payload.title].filter(Boolean).join('\n'),
            errorMessage: result.errorMessage,
          });
          return;
        }

        void logShareImportEvent({
          userId: activeUser.uid,
          outcome: 'exception',
          stage: 'server_request',
          code: 'network_error',
          messageKey: 'shareImportFailedTitle',
          rawPayload: payload,
          extractedUrl: url,
          normalizedUrl: url,
          shareContextText: [payload.text, payload.title].filter(Boolean).join('\n'),
          errorMessage: result.errorMessage,
        });
        await createUserNotification({
          userId: activeUser.uid,
          type: 'share_import_failure',
          title: t('shareImportFailedTitle'),
          body: t('shareImportFailedBody'),
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.error('Share import failed:', error);
        void logShareImportEvent({
          userId: activeUser.uid,
          outcome: 'exception',
          stage: 'run_share_import',
          code: 'exception',
          messageKey: 'shareImportFailedTitle',
          rawPayload: payload,
          extractedUrl: url,
          normalizedUrl: url,
          shareContextText: [payload.text, payload.title].filter(Boolean).join('\n'),
          errorMessage,
        });
        await createUserNotification({
          userId: activeUser.uid,
          type: 'share_import_failure',
          title: t('shareImportFailedTitle'),
          body: t('shareImportFailedBody'),
        });
      } finally {
        clearPendingShareFromStorage();
        shareImportRunningRef.current = false;
      }
    })();
  }, [
    user,
    activeHouseholdId,
    recipes,
    t,
    setGenericNotice,
  ]);

  const tryStartPendingShareImport = useCallback(() => {
    captureShareLaunchFromLocation();
    if (!user?.uid || !activeHouseholdId) return;
    if (shareImportRunningRef.current) return;
    const pendingShare = readPendingShareFromStorage();
    if (!pendingShare) return;
    void runShareImport(pendingShare);
  }, [user?.uid, activeHouseholdId, runShareImport]);

  useEffect(() => {
    tryStartPendingShareImport();
  }, [tryStartPendingShareImport]);

  useEffect(() => {
    const handleResume = () => {
      tryStartPendingShareImport();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleResume();
      }
    };
    window.addEventListener('pageshow', handleResume);
    window.addEventListener('popstate', handleResume);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('pageshow', handleResume);
      window.removeEventListener('popstate', handleResume);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [tryStartPendingShareImport]);

  const handleNotificationClick = useCallback(async (notification: UserNotification) => {
    if (!user?.uid) return;
    if (!notification.read) {
      await markNotificationRead(user.uid, notification.id);
    }
    if (notification.recipeId) {
      const recipe = recipes.find((item) => item.id === notification.recipeId);
      if (recipe) {
        setIsNotificationsOpen(false);
        setRecipeToView(recipe);
        setActiveTab('recipes');
      }
    }
  }, [user?.uid, recipes]);

  const knownItems = useMemo(
      () => buildKnownItems(recipes, shoppingCarts, activeCart, categoryOverrides),
      [recipes, shoppingCarts, activeCart, categoryOverrides],
  );

  const handleAddNewItemToHistory = async (name: string, unit: string, isMeat: boolean, category: string) => {
      if (!activeHouseholdId) return;
      
      const lowercaseName = name.toLowerCase().trim();
      // Check if the item already exists in ANY of the shopping list templates (including any lists the household has)
      const existsInAnyList = shoppingCarts.some(cart => 
          cart.ingredients.some(i => i.name.toLowerCase().trim() === lowercaseName)
      );

      if (existsInAnyList) {
          // If it already exists in any shopping cart template, we shouldn't add it to History/Misc Items list
          return;
      }
      
      let miscCart = shoppingCarts.find(c => c.isMisc);
      
      if (!miscCart) {
          const newCartData = {
              name: 'Miscellaneous Items',
              householdId: activeHouseholdId,
              ownerId: user?.uid || '',
              ingredients: [],
              isMisc: true,
              category: 'General' as ShoppingCategory
          };
          const docRef = await addDoc(collection(db, 'shoppingCarts'), newCartData);
          miscCart = { ...newCartData, id: docRef.id } as ShoppingCart;
      }

      const itemExists = miscCart.ingredients.some(i => i.name.toLowerCase().trim() === lowercaseName);
      
      if (!itemExists) {
          const newItem: Ingredient = {
              name: name,
              quantity: 1,
              unit: unit,
              isMeat: isMeat,
              section: category
          };
          await updateDoc(doc(db, 'shoppingCarts', miscCart.id), {
              ingredients: [...miscCart.ingredients, newItem]
          });
      }
  };

  if (authLoading) {
    return (
      <div
        className="flex h-screen items-center justify-center bg-slate-50 dark:bg-slate-900 text-slate-400 font-rubik"
        dir={isRTL(bootLang) ? 'rtl' : 'ltr'}
      >
        {getTranslation(bootLang, 'appWarmingUp')}
      </div>
    );
  }
  if (!user) {
    if (pendingAccessToken && !useStandardLogin) {
      return (
        <GuidedAccessLogin
          accessToken={pendingAccessToken}
          onSuccess={() => {
            setPendingAccessToken(null);
            setAuthLoading(true);
          }}
          onUseStandardLogin={() => setUseStandardLogin(true)}
        />
      );
    }
    if (!showLogin) {
      return (
        <LandingPage onLoginClick={() => setShowLogin(true)} />
      );
    }
    return (
      <Login
        onLogin={() => {}}
        users={[]}
        authError={authInitError}
        onClearAuthError={() => setAuthInitError(null)}
        onBack={() => setShowLogin(false)}
      />
    );
  }

  const guidedUser = isGuidedUser(user);

  return (
    <div className="flex justify-center items-center h-[100dvh] w-full bg-slate-100 dark:bg-slate-950 sm:bg-[#f0f0ed] sm:dark:bg-[#0f172a] font-rubik overflow-hidden">
        <div 
            id="app-container"
            className={`flex flex-col w-full h-full sm:max-w-md sm:h-[95vh] sm:rounded-[2.5rem] sm:border-[8px] sm:border-slate-800 relative overflow-hidden bg-slate-100 dark:bg-slate-950 text-slate-800 dark:text-slate-100 sm:shadow-2xl transition-all`} 
            dir={user.language === 'he' || user.language === 'ar' ? 'rtl' : 'ltr'}
        >
            <Header 
                user={user}
                authUser={authUser}
                isImpersonating={isImpersonating}
                onExitImpersonation={handleExitImpersonation}
                activeHouseholdId={activeHouseholdId} households={households} recipes={recipes}
                onSwitchHousehold={handleSwitchHousehold}
                onLogout={() => {
                  setImpersonatedUserId(null);
                  setConsultantClientContext(null);
                  clearGuidedSession();
                  signOut(auth);
                }}
                onUserManagementClick={() => setIsUserManagementOpen(true)}
                onHouseholdManagementClick={() => {
                  if (!guidedUser) setIsHouseholdManagementOpen(true);
                }}
                onRecipeSelect={(r) => {
                  if (guidedUser && !canGuidedUserViewRecipe(r, mealPlan, activeCart, Object.values(reminderMealPlans))) {
                    return;
                  }
                  setRecipeToView(r);
                  if (!guidedUser) setActiveTab('recipes');
                }}
                onPersonalDetailsClick={() => setIsPersonalDetailsOpen(true)}
                onSettingsClick={() => setIsSettingsOpen(true)}
                onSubscriptionManagementClick={() => setIsSubscriptionManagementOpen(true)}
                onTosClick={() => setIsTosOpen(true)}
                onContactClick={() => setIsContactOpen(true)}
                onHowToClick={() => setIsHowToOpen(true)}
                onSuggestionBoxClick={() => setIsSuggestionBoxOpen(true)}
                onConsultationBoxClick={guidedUser ? () => setIsConsultationOpen(true) : undefined}
                onSuggestionManagementClick={() => setIsSuggestionManagementOpen(true)}
                onChefManagementClick={() => setIsChefManagementOpen(true)}
                onGuidedClientsClick={() => setIsGuidedClientsOpen(true)}
                onPhraseOfDayAdminClick={canManagePhrases ? () => setIsPhraseAdminOpen(true) : undefined}
                onOpenReminders={() => setIsRemindersOpen(true)}
                onOpenActiveCart={() => setIsActiveCartOpen(true)}
                hasReminders={defrostReminders.length > 0}
                onNotificationsClick={() => setIsNotificationsOpen(true)}
                unreadNotificationCount={unreadNotificationCount}
                onTabChange={setActiveTab}
                showFastingUi={showFastingUi}
                fastingActive={intermittentFasting.snapshot.status === 'active'}
                fastingScheduled={intermittentFasting.snapshot.status === 'scheduled'}
                fastingRemainingMs={intermittentFasting.remainingMs}
                onOpenFastingTrackerModal={() => setIsFastingTrackerModalOpen(true)}
                pendingSubscriptionCount={
                  authUser?.role === 'superuser' ? countPendingSubscriptionApprovals(allUsers) : 0
                }
                newSuggestionsCount={
                  authUser?.role === 'superuser' ? countNewSuggestions(suggestions) : 0
                }
            />

            {invitedHouseholds.map(hh => (
                <div key={hh.id} className="bg-sky-50 dark:bg-sky-900/20 border-b border-sky-100 dark:border-sky-800/50 p-3 flex items-center justify-between gap-3 text-xs sm:text-sm animate-fade-in shrink-0">
                    <div className="flex items-center gap-2 min-w-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-sky-500 shrink-0">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                        </svg>
                        <span className="font-semibold text-slate-800 dark:text-slate-200 truncate">
                            {t('householdInvitationMsg').replace('{name}', hh.name)}
                        </span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                        <button 
                            type="button"
                            onClick={() => handleAcceptInvitation(hh.id)}
                            className="px-3 py-1.5 bg-brand-sage text-white font-bold rounded-lg hover:opacity-90 active:scale-95 transition-all text-xs"
                        >
                            {t('approveInvitation')}
                        </button>
                        <button 
                            type="button"
                            onClick={() => handleRejectInvitation(hh.id)}
                            className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg active:scale-95 transition-all text-xs"
                        >
                            {t('rejectInvitation')}
                        </button>
                    </div>
                </div>
            ))}

            {guidedUser && consultantBannerData?.isReady && (
                <ConsultantBanner
                    data={consultantBannerData}
                    language={user.language}
                    onOpenChat={() => setIsConsultationOpen(true)}
                />
            )}

            {consultantClientContext && (
                <div className="px-4 py-2 bg-emerald-50 dark:bg-emerald-900/20 border-b border-emerald-100 dark:border-emerald-900 flex items-center justify-between gap-2">
                    <span className="text-xs font-bold text-emerald-800 dark:text-emerald-200 truncate">
                        {getTranslation(user.language, 'guidedClientContextBanner')} {consultantClientContext.clientName}
                    </span>
                    <button
                        type="button"
                        onClick={handleExitClientContext}
                        className="text-[10px] font-black uppercase text-emerald-700 dark:text-emerald-300 shrink-0"
                    >
                        {getTranslation(user.language, 'exitClientContext')}
                    </button>
                </div>
            )}

            {isImpersonating && authUser && (
                <ImpersonationBanner
                    viewUser={user}
                    language={authUser.language}
                    onExitImpersonation={handleExitImpersonation}
                />
            )}

            <div className="flex-grow overflow-hidden relative">
                {activeTab === 'plan' && (
                    <PlanScreen
                        language={user.language}
                        weeklyProps={{
                            mealPlan, recipes, role: user.role, householdRole: householdRole as HouseholdRole,
                            weekOffset, currentUserId: user.uid, activeHouseholdId, activeHousehold,
                            onSetWeekOffset: setWeekOffset,
                            onAddRecipesToDay: async (ids, day, type) => { try { await handleAddRecipeToDay(ids, day, type); } catch (e) {} },
                            onScheduleMeal: async (rid, day, type, servings, _status, offset) => {
                                try { await handleAddRecipeToDay([rid], day, type, offset, servings); } catch (e) {}
                            },
                            onRemoveRecipeFromDay: (pmId, day) => handleRemoveRecipeFromDay(pmId, day, weekOffset),
                            onMoveMeal: () => {},
                            onUpdateMeal: async (pmId, day, updates) => { try { await handleUpdateMeal(pmId, day, updates, weekOffset); } catch (e) {} },
                            onGenerateWeeklyShoppingList: () => {},
                            onGenerateMealShoppingList: () => {},
                            onOpenMeatList: () => {},
                            onOpenReminders: () => setIsRemindersOpen(true),
                            onEditRecipe: (id) => { setRecipeToEdit(recipes.find(r => r.id === id)); setIsAddRecipeOpen(true); },
                            onDeleteRecipe: async (id) => { await deleteDoc(doc(db, 'recipes', id)); },
                            onCopyCurrentWeekToNext: () => {},
                            onRecipeClick: (r) => setRecipeToView(r),
                            visibleMealTypes: (activeHousehold?.visibleMealTypes as MealType[]) || ['Breakfast', 'Lunch', 'Dinner'],
                            language: user.language, orderedDays: orderedDays as DayOfWeek[],
                            onCookMeal: (meals, type, day, offset, slotMenuTitles, initialRecipeId) => setCookingSessionData({
                                meals,
                                type,
                                day,
                                weekOffset: offset,
                                slotMenuTitles: slotMenuTitles ?? mealPlan.slotMenuTitles,
                                initialRecipeId,
                            }),
                            onUpdateMealSlotTitle: (day, type, title) => {
                                void handleUpdateMealSlotTitle(day, type, title, weekOffset);
                            },
                            onToggleFavoriteRecipe: handleToggleFavoriteRecipe,
                            onCreateRecipe: handleQuickCreateRecipe,
                            onEditMeal: (meal) => setAddMealData({ 
                                recipe: meal.recipe, 
                                day: orderedDays.includes(getDayFromMeal(mealPlan, meal.id, weekOffset) || 'Sunday') ? getDayFromMeal(mealPlan, meal.id, weekOffset)! : 'Sunday', 
                                type: meal.mealType || 'Dinner',
                                offset: weekOffset,
                                plannedMealId: meal.id,
                                currentServings: meal.plannedServings,
                                currentStatus: meal.sourcingStatus,
                                originalDay: getDayFromMeal(mealPlan, meal.id, weekOffset),
                                originalOffset: weekOffset,
                                originalType: meal.mealType || 'Dinner',
                            }),
                            activeCart,
                            onUpdateActiveCart: handleUpdateActiveCart,
                            categoryOverrides,
                            userMealPreferences,
                            chefs,
                            allUsers: allUsers.length > 0 ? allUsers : [user]
                        }}
                        monthlyProps={{
                            activeHouseholdId, activeHousehold, recipes, language: user.language,
                            onGoToWeek: (offset) => setWeekOffset(offset),
                            role: user.role, householdRole: householdRole as HouseholdRole,
                            onAddRecipesToDay: async (ids, day, type, offset) => { try { await handleAddRecipeToDay(ids, day, type, offset); } catch (e) {} },
                            onScheduleMeal: async (rid, day, type, servings, _status, offset) => {
                                try { await handleAddRecipeToDay([rid], day, type, offset, servings); } catch (e) {}
                            },
                            onRemoveRecipeFromDay: (pmId, day, offset) => handleRemoveRecipeFromDay(pmId, day, offset),
                            onMoveMeal: () => {},
                            onUpdateMeal: async (pmId, day, updates, offset) => { try { await handleUpdateMeal(pmId, day, updates, offset); } catch (e) {} },
                            onRecipeClick: (r) => setRecipeToView(r),
                            onCookMeal: (meals, type, day, offset, slotMenuTitles, initialRecipeId) => setCookingSessionData({
                                meals,
                                type,
                                day,
                                weekOffset: offset,
                                slotMenuTitles,
                                initialRecipeId,
                            }),
                            onUpdateMealSlotTitle: (day, type, title, offset) => {
                                void handleUpdateMealSlotTitle(day, type, title, offset ?? weekOffset);
                            },
                            visibleMealTypes: (activeHousehold?.visibleMealTypes as MealType[]) || ['Breakfast', 'Lunch', 'Dinner'],
                            weekStartDay: user.weekStartDay, activeCart, onUpdateActiveCart: handleUpdateActiveCart,
                            onToggleFavoriteRecipe: handleToggleFavoriteRecipe,
                            onCreateRecipe: handleQuickCreateRecipe,
                            onEditMeal: (meal, day, offset) => {
                                setAddMealData({ 
                                    recipe: meal.recipe, 
                                    day: day, 
                                    type: meal.mealType || 'Dinner',
                                    offset: offset,
                                    plannedMealId: meal.id,
                                    currentServings: meal.plannedServings,
                                    currentStatus: meal.sourcingStatus,
                                    originalDay: day,
                                    originalOffset: offset,
                                    originalType: meal.mealType || 'Dinner',
                                })
                            },
                            categoryOverrides,
                            currentUserId: user.uid,
                            userMealPreferences,
                            chefs,
                            allUsers: allUsers.length > 0 ? allUsers : [user]
                        }}
                    />
                )}
                {activeTab === 'recipes' && (
                    <RecipeManager 
                        recipes={recipes} activeCart={activeCart} onUpdateActiveCart={handleUpdateActiveCart}
                        role={user.role} householdRole={householdRole as HouseholdRole}
                        onAddRecipe={() => { setRecipeToEdit(undefined); setIsAddRecipeOpen(true); }}
                        onDeleteRecipe={async (id) => { await deleteDoc(doc(db, 'recipes', id)); }}
                        onEditRecipe={(id) => {
                            const recipe = recipes.find(r => r.id === id);
                            if (!recipe || isRecipeSavePending(recipe)) return;
                            setRecipeToEdit(recipe);
                            setIsAddRecipeOpen(true);
                        }}
                        onViewRecipe={(r) => {
                            if (isRecipeSavePending(r)) return;
                            setRecipeToView(r);
                        }}
                        onScheduleRecipe={(r) => {
                            if (isRecipeSavePending(r)) return;
                            const slot = getDefaultScheduleSlot((activeHousehold?.visibleMealTypes as MealType[]) || ['Breakfast', 'Lunch', 'Dinner']);
                            setAddMealData({ recipe: r, day: slot.day, type: slot.mealType });
                        }}
                        onAddRecipesToDay={async (ids, day, type) => { try { await handleAddRecipeToDay(ids, day, type); } catch (e) {} }}
                        currentUserId={user.uid} activeHouseholdId={activeHouseholdId} allUsers={allUsers.length > 0 ? allUsers : [user]}
                        chefs={chefs}
                        userMealPreferences={userMealPreferences}
                        onToggleFavoriteRecipe={handleToggleFavoriteRecipe}
                        visibleMealTypes={(activeHousehold?.visibleMealTypes as MealType[]) || ['Breakfast', 'Lunch', 'Dinner']}
                        language={user.language} orderedDays={orderedDays as DayOfWeek[]}
                        pendingChefFilterId={pendingChefFilterId}
                        onPendingChefFilterApplied={() => setPendingChefFilterId(null)}
                        activeHousehold={activeHousehold}
                    />
                )}
                {activeTab === 'lists' && (
                    <ListTab 
                        shoppingCarts={shoppingCarts} activeCart={activeCart} onUpdateActiveCart={handleUpdateActiveCart}
                        onAddCart={() => { setCartToEdit(undefined); setIsAddCartOpen(true); }}
                        onViewCart={(cart) => setCartToView(cart)}
                        onEditCart={(cart) => { setCartToEdit(cart); setIsAddCartOpen(true); }}
                        onDeleteCart={async (id) => { if(window.confirm('Delete Cart?')) await deleteDoc(doc(db, 'shoppingCarts', id)); }}
                        onUpdateCart={async (cart) => { await updateDoc(doc(db, 'shoppingCarts', cart.id), cart as any); }}
                        canManage={householdRole !== 'viewer'} language={user.language}
                    />
                )}
            </div>

            <BottomNav activeTab={activeTab} onTabChange={setActiveTab} language={user.language} isGuidedUser={guidedUser} />
            <CookieConsent />
            <InstallAppPrompt language={user.language} isAuthenticated />

            {showFastingUi && (
                <>
                    <FastingTrackerModal
                        isOpen={isFastingTrackerModalOpen}
                        onClose={() => setIsFastingTrackerModalOpen(false)}
                        isActive={intermittentFasting.snapshot.status === 'active'}
                        isScheduled={intermittentFasting.snapshot.status === 'scheduled'}
                        goalHours={intermittentFasting.snapshot.goalHours}
                        language={user.language}
                        isBusy={intermittentFasting.isBusy}
                        remainingMs={intermittentFasting.remainingMs}
                        endTimestamp={intermittentFasting.endTimestamp}
                        startedAt={intermittentFasting.snapshot.startedAt}
                        scheduledStartAt={intermittentFasting.snapshot.scheduledStartAt}
                        onStart={intermittentFasting.startFast}
                        onEndFast={intermittentFasting.endFast}
                        onSchedule={intermittentFasting.scheduleFast}
                        onCancelSchedule={intermittentFasting.cancelScheduledFast}
                        onGoalChange={intermittentFasting.setGoalHours}
                        onUpdateStartTime={intermittentFasting.updateStartTime}
                        onOpenStatistics={() => {
                            setIsFastingTrackerModalOpen(false);
                            setIsFastingStatisticsOpen(true);
                        }}
                    />

                    <FastingStatisticsModal
                        isOpen={isFastingStatisticsOpen}
                        onClose={() => setIsFastingStatisticsOpen(false)}
                        userId={fastingUserId}
                        language={user.language}
                    />
                </>
            )}

            {isAddRecipeOpen && (
                <AddRecipeModal 
                    onClose={() => {
                        setRecipeToEdit(undefined);
                        if (previewFromScheduler && addMealData) {
                            setIsAddRecipeOpen(false);
                            return;
                        }
                        if (recipeToView || addMealData || cookingSessionData) setIsAddRecipeOpen(false);
                        else handleCloseModal(() => setIsAddRecipeOpen(false));
                    }} 
                    currentUser={user} recipeToEdit={recipeToEdit}
                    households={households}
                    chefs={chefs}
                    allRecipes={recipes}
                    canManage={user.role === 'superuser' || ['owner', 'admin', 'editor'].includes(householdRole as string)}
                    onAddRecipe={async (r) => {
                        const guarded = guardTemporaryRecipe(r);
                        const docRef = await addDoc(collection(db, 'recipes'), stripUndefinedFields({
                            ...guarded,
                            householdId: activeHouseholdId,
                            createdAt: new Date().toISOString(),
                        }));
                        return docRef.id;
                    }}
                    onRecipeSaved={(recipe) => {
                        setRecipeToEdit(undefined);
                        setIsAddRecipeOpen(false);
                        if (isRecipeSavePending(recipe)) {
                            // Stay on recipes list with the pending card; user may leave freely.
                            setActiveTab('recipes');
                            return;
                        }
                        setRecipeToView(recipe);
                    }}
                    onBackgroundFinalize={(recipe) => {
                        void runBackgroundRecipeFinalize(recipe);
                    }}
                    onUpdateRecipe={async (r) => {
                        const guarded = guardTemporaryRecipe(r);
                        await updateDoc(
                            doc(db, 'recipes', r.id),
                            recipeUpdatePayload(guarded as unknown as Record<string, unknown>),
                        );
                    }}
                    onDeleteRecipe={async (id) => { await deleteDoc(doc(db, 'recipes', id)); }}
                    onResolveInstagramChef={(owner: InstagramOwner) => resolveInstagramChef(owner, chefs)}
                    onViewExistingRecipe={(r) => {
                        setRecipeToEdit(undefined);
                        setIsAddRecipeOpen(false);
                        setRecipeToView(r);
                    }}
                    onSaveUserProfile={async (updates) => {
                        if (Object.keys(updates).length > 0) {
                            await updateDoc(doc(db, 'users', user.uid), updates);
                        }
                        const nextUser = { ...user, ...updates };
                        setUser(nextUser);
                        if (!isImpersonating && authUser) {
                            setAuthUser({ ...authUser, ...nextUser });
                        }
                    }}
                />
            )}
            {isChefAssistantOpen && (
                <ChefAssistantModal 
                    onClose={() => handleCloseModal(() => setIsChefAssistantOpen(false))} 
                    currentUser={user}
                    chefs={chefs}
                    allRecipes={recipes}
                    onResolveInstagramChef={(owner: InstagramOwner) => resolveInstagramChef(owner, chefs)}
                    onViewExistingRecipe={(r) => {
                        setIsChefAssistantOpen(false);
                        setRecipeToView(r);
                    }}
                    onAddRecipe={async (r) => {
                        const guarded = guardTemporaryRecipe(r);
                        const docRef = await addDoc(collection(db, 'recipes'), stripUndefinedFields({
                            ...guarded,
                            householdId: activeHouseholdId,
                            createdAt: new Date().toISOString(),
                        }));
                        if (isRecipeSavePending(r as Recipe)) {
                            void runBackgroundRecipeFinalize({ ...r, id: docRef.id } as Recipe);
                        }
                        return docRef.id;
                    }}
                />
            )}
            {recipeToView && (
                <RecipeDetailsModal 
                    recipe={recipes.find(r => r.id === recipeToView.id) || recipeToView} role={user.role} householdRole={householdRole as HouseholdRole} currentUserId={user.uid}
                    activeHouseholdId={activeHouseholdId}
                    onClose={() => {
                        if (addMealData || cookingSessionData) { setRecipeToView(null); }
                        else handleCloseModal(() => { setRecipeToView(null); setPreviewFromScheduler(false); });
                    }}
                    onEdit={() => {
                        if (!recipeToView) return;
                        setRecipeToEdit(recipes.find((r) => r.id === recipeToView.id) || recipeToView);
                        setRecipeToView(null);
                        setIsAddRecipeOpen(true);
                    }}
                    onDelete={async () => { await deleteDoc(doc(db, 'recipes', recipeToView.id)); handleCloseModal(() => setRecipeToView(null)); }}
                    onOpenAddMeal={() => {
                        const slot = getDefaultScheduleSlot((activeHousehold?.visibleMealTypes as MealType[]) || ['Breakfast', 'Lunch', 'Dinner']);
                        setAddMealData({ recipe: recipeToView, day: slot.day, type: slot.mealType });
                    }}
                    onAddIngredientsToCart={async (ings, recipeName) => addIngredientsToActiveCart(ings, recipeName, 'recipe')}
                    language={user.language} orderedDays={orderedDays as DayOfWeek[]}
                    visibleMealTypes={(activeHousehold?.visibleMealTypes as MealType[]) || ['Breakfast', 'Lunch', 'Dinner']}
                    allUsers={allUsers.length > 0 ? allUsers : [user]}
                    viewingUser={user}
                    chefs={chefs}
                    onViewChef={(chefId) => {
                        const c = chefs.find((x) => x.id === chefId);
                        if (c) setChefToView(c);
                    }}
                    userMealPreferences={userMealPreferences}
                    onToggleFavoriteRecipe={handleToggleFavoriteRecipe}
                    showDuplicateForHousehold={
                        !!consultantClientContext
                        && recipeToView.householdId !== activeHouseholdId
                    }
                    onDuplicateForHousehold={async () => {
                        if (!recipeToView || !activeHouseholdId || !user) return;
                        const duplicate = guardTemporaryRecipe(
                            buildDuplicateRecipeForHousehold(recipeToView, activeHouseholdId, user.uid),
                        );
                        const ref = await addDoc(collection(db, 'recipes'), stripUndefinedFields(duplicate));
                        const created = { ...duplicate, id: ref.id } as Recipe;
                        setRecipeToView(created);
                    }}
                    guidedMode={guidedUser}
                />
            )}
            {isAddCartOpen && (
                <AddShoppingCartModal 
                    isOpen={isAddCartOpen} onClose={() => handleCloseModal(() => setIsAddCartOpen(false))}
                    onAddCart={async (cart) => { await addDoc(collection(db, 'shoppingCarts'), { ...cart, householdId: activeHouseholdId, ownerId: user.uid }); }}
                    onUpdateCart={async (cart) => { await updateDoc(doc(db, 'shoppingCarts', cart.id), cart as any); }}
                    cartToEdit={cartToEdit} language={user.language}
                />
            )}
            {cartToView && (
                <ShoppingCartDetailsModal 
                    cart={shoppingCarts.find(c => c.id === cartToView.id) || cartToView} onClose={() => handleCloseModal(() => setCartToView(null))}
                    onEdit={() => { setCartToEdit(cartToView); setCartToView(null); setIsAddCartOpen(true); }}
                    language={user.language} allCarts={shoppingCarts} activeCart={activeCart}
                    onUpdateActiveCart={handleUpdateActiveCart}
                    onDeleteCart={async (id) => { await deleteDoc(doc(db, 'shoppingCarts', id)); }}
                    onUpdateCartIngredients={async (id, ingredients) => { await updateDoc(doc(db, 'shoppingCarts', id), { ingredients }); }}
                    onDeleteItems={async (cartId, names) => {
                        const cart = shoppingCarts.find(c => c.id === cartId);
                        if (!cart) return;
                        const namesSet = new Set(names);
                        const nextIngs = (cart.ingredients ?? []).filter(i => !namesSet.has(i.name));
                        await updateDoc(doc(db, 'shoppingCarts', cartId), { ingredients: nextIngs });
                    }}
                    onMoveItems={async (ingsToMove, fromCartId, toCartId) => {
                        const sourceCart = shoppingCarts.find(c => c.id === fromCartId);
                        const targetCart = shoppingCarts.find(c => c.id === toCartId);
                        if (!sourceCart || !targetCart) return;
                        const namesToMove = new Set(ingsToMove.map(i => i.name));
                        const nextSourceIngs = (sourceCart.ingredients ?? []).filter(i => !namesToMove.has(i.name));
                        const nextTargetIngs = [...(targetCart.ingredients ?? []), ...ingsToMove];
                        await updateDoc(doc(db, 'shoppingCarts', fromCartId), { ingredients: nextSourceIngs });
                        await updateDoc(doc(db, 'shoppingCarts', toCartId), { ingredients: nextTargetIngs });
                    }}
                    onCreateAndMoveItems={async (ingsToMove, fromCartId, newCartName) => {
                        const sourceCart = shoppingCarts.find(c => c.id === fromCartId);
                        if (!sourceCart) return;
                        const namesToMove = new Set(ingsToMove.map(i => i.name));
                        const nextSourceIngs = (sourceCart.ingredients ?? []).filter(i => !namesToMove.has(i.name));
                        await addDoc(collection(db, 'shoppingCarts'), {
                            name: newCartName,
                            ingredients: ingsToMove,
                            category: sourceCart.category || 'General',
                            householdId: activeHouseholdId,
                            ownerId: user.uid,
                        });
                        await updateDoc(doc(db, 'shoppingCarts', fromCartId), { ingredients: nextSourceIngs });
                    }}
                />
            )}
            <ActiveShoppingCartModal 
                isOpen={isActiveCartOpen} onClose={() => handleCloseModal(() => setIsActiveCartOpen(false))}
                activeCart={activeCart} onUpdateCart={handleUpdateActiveCart}
                language={user.language} knownItems={knownItems}
                onNewItemAdded={handleAddNewItemToHistory} shoppingCarts={shoppingCarts}
                categoryOverrides={categoryOverrides}
                onUpdateCategoryKnowledge={handleUpdateCategoryKnowledge}
                savedExpandedSections={activeCartSavedExpandedSections}
                hasSavedSectionState={hasActiveCartSavedSectionState}
                onSaveExpandedSections={handleSaveActiveCartExpandedSections}
            />
            {addMealData && (
                <AddMealModal 
                    isOpen={!!addMealData && !plannedRecipePreview && !(previewFromScheduler && (recipeToView || isAddRecipeOpen))}
                    onClose={() => {
                        setPreviewFromScheduler(false);
                        handleCloseModal(() => setAddMealData(null));
                    }}
                    recipe={recipes.find((r) => r.id === addMealData.recipe.id) || addMealData.recipe}
                    initialDay={addMealData.day} 
                    initialType={addMealData.type} 
                    weekOffset={addMealData.offset ?? weekOffset}
                    initialServings={addMealData.currentServings}
                    initialStatus={addMealData.currentStatus}
                    isEditing={!!addMealData.plannedMealId}
                    readOnlySchedule={guidedUser && !!addMealData.plannedMealId}
                    onConfirm={async (rid, day, type, servings, status, offset, isUpdate) => {
                        if (guidedUser && addMealData.plannedMealId && isUpdate) {
                            const pmId = addMealData.plannedMealId;
                            const origDay = addMealData.originalDay ?? addMealData.day;
                            const origOffset = addMealData.originalOffset ?? addMealData.offset ?? weekOffset;
                            await handleUpdateMeal(pmId, origDay, { servings, status }, origOffset);
                            return;
                        }
                        if (guidedUser) return;

                        if (addMealData.plannedMealId && isUpdate) {
                            const pmId = addMealData.plannedMealId;
                            const origDay = addMealData.originalDay ?? addMealData.day;
                            const origOffset = addMealData.originalOffset ?? addMealData.offset ?? weekOffset;

                            if (origOffset !== offset) {
                                await handleReschedulePlannedMeal(pmId, origDay, origOffset, day, type, offset, { servings, status });
                            } else if (origDay !== day) {
                                await handleMoveMeal(pmId, origDay, day, type, offset);
                                await handleUpdateMeal(pmId, day, { servings, status, mealType: type }, offset);
                            } else {
                                await handleUpdateMeal(pmId, day, { servings, status, mealType: type }, offset);
                            }
                        } else {
                            await handleAddRecipeToDay([rid], day, type, offset, servings);
                        }
                    }}
                    onDelete={guidedUser ? undefined : () => {
                        if (addMealData.plannedMealId && addMealData.originalDay) {
                            handleRemoveRecipeFromDay(addMealData.plannedMealId, addMealData.originalDay, addMealData.originalOffset);
                        }
                    }}
                    onViewRecipe={(r, mealServings) => {
                        const fresh = recipes.find((x) => x.id === r.id) || r;
                        setPlannedRecipePreview({
                            recipe: fresh,
                            plannedServings: mealServings,
                            day: addMealData.day,
                            mealType: addMealData.type,
                            weekOffset: addMealData.offset ?? weekOffset,
                        });
                    }}
                    language={user.language} orderedDays={orderedDays as DayOfWeek[]}
                    visibleMealTypes={(activeHousehold?.visibleMealTypes as MealType[]) || ['Breakfast', 'Lunch', 'Dinner']}
                />
            )}
            {scheduledConflict && (
                <InfoNoticeModal
                    isOpen={!!scheduledConflict}
                    onClose={() => setScheduledConflict(null)}
                    title={t('recipeAlreadyScheduledTitle')}
                    message={`${scheduledConflict.recipeName} ${t('recipeAlreadyScheduledMsg')} ${t(DAY_TRANSLATIONS[scheduledConflict.day])} (${t(scheduledConflict.mealType.toLowerCase() as any)})`}
                    variant="error"
                    language={user?.language}
                />
            )}
            {genericNotice && (
                <InfoNoticeModal
                    isOpen={!!genericNotice}
                    onClose={() => setGenericNotice(null)}
                    title={genericNotice.title}
                    message={genericNotice.message}
                    variant={genericNotice.variant || 'error'}
                    language={user?.language}
                />
            )}
            <UserManagementModal 
                isOpen={isUserManagementOpen} 
                onClose={() => handleCloseModal(() => setIsUserManagementOpen(false))}
                users={allUsers} 
                currentUser={authUser!}
                onImpersonateUser={authUser?.role === 'superuser' ? handleImpersonateUser : undefined}
                onUpdateUserRole={async (email, role) => {
                    const u = allUsers.find(u => u.email === email);
                    if (u) await updateDoc(doc(db, 'users', u.uid), { role });
                }}
                onUpdateUserType={async (email, userType) => {
                    const u = allUsers.find(u => u.email === email);
                    if (u) await updateDoc(doc(db, 'users', u.uid), { userType });
                }}
                onUpdateHouseholdEntitlement={async (uid, entitlement) => {
                    await updateDoc(doc(db, 'users', uid), {
                        householdEntitlement: Math.max(1, Math.floor(entitlement)),
                    });
                }}
                onUpdateAiEligibility={async (uid, isAiEligible) => {
                    await updateDoc(doc(db, 'users', uid), { isAiEligible });
                }}
                onGenerateApiKey={async (uid) => {
                    const newKey = 'mp_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
                    await updateDoc(doc(db, 'users', uid), { apiKey: newKey });
                }}
                recipes={recipes} 
                households={households}
            />
            <HouseholdManagementModal 
                isOpen={isHouseholdManagementOpen} onClose={() => handleCloseModal(() => setIsHouseholdManagementOpen(false))}
                currentUser={user} activeHouseholdId={activeHouseholdId} allUsers={allUsers} households={households}
                onCreateHousehold={handleCreateHousehold} onAddMember={handleAddMember} onRemoveMember={handleRemoveMember} onUpdateMemberRole={handleUpdateMemberRole}
                onDeleteHousehold={handleDeleteHousehold} onUpdateHouseholdConfig={async (hhId, updates) => { await updateDoc(doc(db, 'households', hhId), updates); }}
                onSwitchHousehold={(id) => { handleSwitchHousehold(id); handleCloseModal(() => setIsHouseholdManagementOpen(false)); }}
            />
            <SubscriptionManagementModal 
                isOpen={isSubscriptionManagementOpen} onClose={() => handleCloseModal(() => setIsSubscriptionManagementOpen(false))}
                users={allUsers}
                language={user.language}
            />
            <SettingsModal 
                isOpen={isSettingsOpen} onClose={() => handleCloseModal(() => setIsSettingsOpen(false))} user={user}
                guidedMode={guidedUser}
                onSave={async (updates) => {
                    const hasNutritionUpdate =
                        updates.knownAllergies !== undefined || updates.userDietTypes !== undefined;

                    if (guidedUser && user.guidedClientId && hasNutritionUpdate) {
                        await updateGuidedClientSelf(user.guidedClientId, {
                            knownAllergies: updates.knownAllergies,
                            userDietTypes: updates.userDietTypes,
                        });
                    }

                    const userPatch = { ...updates };
                    if (guidedUser && hasNutritionUpdate) {
                        delete userPatch.knownAllergies;
                        delete userPatch.userDietTypes;
                    }

                    if (Object.keys(userPatch).length > 0) {
                        await updateDoc(doc(db, 'users', user.uid), userPatch);
                    }

                    const nextUser = { ...user, ...updates };
                    if (updates.language) {
                        persistAppLanguage(updates.language);
                        setBootLang(updates.language);
                    }
                    setUser(nextUser);
                    if (!isImpersonating && authUser) {
                        setAuthUser({ ...authUser, ...nextUser });
                    }
                    await syncPreferredDietFromUser(user.uid, nextUser);
                }}
            />
            <PersonalDetailsModal 
                isOpen={isPersonalDetailsOpen} onClose={() => handleCloseModal(() => setIsPersonalDetailsOpen(false))} user={user}
                guidedMode={guidedUser}
                chefs={chefs}
                onSave={async (updates) => {
                    await updateDoc(doc(db, 'users', user.uid), updates);
                    setUser((prev) => prev ? { ...prev, ...updates } : null);
                    if (!isImpersonating && authUser) {
                        setAuthUser({ ...authUser, ...updates });
                    }
                }}
                onGoPro={guidedUser ? () => { setIsPersonalDetailsOpen(false); setIsGoProOpen(true); } : undefined}
                onEmailChange={guidedUser && user.guidedClientId ? async (email) => {
                    const snap = await getDocs(query(collection(db, 'guidedClientAccess'), where('clientId', '==', user.guidedClientId!)));
                    const accessId = snap.docs[0]?.id;
                    if (!accessId) return {};
                    return changeGuidedClientEmail(user.guidedClientId!, accessId, email);
                } : undefined}
                onRequestChefRecognition={guidedUser ? undefined : () => { setIsPersonalDetailsOpen(false); setIsChefRequestOpen(true); }}
                onViewChef={(chefId) => {
                    const c = chefs.find((x) => x.id === chefId);
                    if (c) {
                        setIsPersonalDetailsOpen(false);
                        setChefToView(c);
                    }
                }}
            />
            <ChefRequestModal
                isOpen={isChefRequestOpen}
                onClose={() => handleCloseModal(() => setIsChefRequestOpen(false))}
                user={user}
                chefs={chefs}
                chefRequests={chefRequests}
                onSubmit={async (data) => {
                    await addDoc(collection(db, 'chefRequests'), stripUndefinedFields({
                        ...data,
                        userId: user.uid,
                        userEmail: user.email,
                        status: 'pending',
                        createdAt: new Date().toISOString(),
                    }));
                }}
            />
            <ChefProfileModal
                isOpen={!!chefToView}
                onClose={() => setChefToView(null)}
                chef={chefToView}
                recipes={recipes}
                language={user.language}
                userMealPreferences={userMealPreferences}
                onToggleFavoriteChef={handleToggleFavoriteChef}
                onViewRecipes={(chefId) => {
                    setChefToView(null);
                    setRecipeToView(null);
                    setPreviewFromScheduler(false);
                    setIsPersonalDetailsOpen(false);
                    setPendingChefFilterId(chefId);
                    setActiveTab('recipes');
                }}
                onEdit={
                    chefToView?.linkedUserId === user.uid
                        ? () => setLinkedChefToEdit(chefToView)
                        : undefined
                }
            />
            <ChefEditModal
                isOpen={!!linkedChefToEdit}
                onClose={() => setLinkedChefToEdit(null)}
                chef={linkedChefToEdit}
                linkedUserEdit
                language={user.language}
                onSave={async (id, updates) => {
                    await updateDoc(doc(db, 'chefs', id), updates as Record<string, unknown>);
                    setChefToView((prev) => (prev?.id === id ? { ...prev, ...updates } : prev));
                }}
            />
            <ChefManagementModal
                isOpen={isChefManagementOpen}
                onClose={() => handleCloseModal(() => setIsChefManagementOpen(false))}
                user={user}
                chefs={chefs}
                chefRequests={chefRequests}
                recipes={recipes}
                onUpdateChef={async (id, updates) => {
                    await updateDoc(doc(db, 'chefs', id), updates as Record<string, unknown>);
                }}
                onCreateChef={async (chef) => {
                    await setDoc(doc(db, 'chefs', chef.id), chef);
                }}
                onMergeChefs={async (keepId, mergeId) => {
                    const keepChef = chefs.find((c) => c.id === keepId);
                    const mergeChef = chefs.find((c) => c.id === mergeId);
                    if (!keepChef || !mergeChef) throw new Error('Chef not found');
                    const result = await mergeChefsInFirestore(db, keepId, mergeId, keepChef, mergeChef);
                    return formatMergeSummary(result, mergeChef, keepChef, user.language || 'he');
                }}
                onApproveRequest={async (request) => {
                    const reviewedAt = new Date().toISOString();
                    let chefId: string;
                    if (request.requestType === 'existing' && request.targetChefId) {
                        chefId = request.targetChefId;
                        const chefRef = doc(db, 'chefs', chefId);
                        const chefSnap = await getDoc(chefRef);
                        if (!chefSnap.exists()) throw new Error('Chef not found');
                        const chefData = chefSnap.data() as Chef;
                        if (chefData.linkedUserId && chefData.linkedUserId !== request.userId) {
                            throw new Error('Chef already linked to another user');
                        }
                        await updateDoc(chefRef, { linkedUserId: request.userId, updatedAt: reviewedAt });
                    } else {
                        const userSnap = await getDoc(doc(db, 'users', request.userId));
                        const userNickname = userSnap.exists() ? (userSnap.data() as User).nickname : undefined;
                        const newChef = buildChefFromRequest(request, userNickname);
                        chefId = newChef.id;
                        await setDoc(doc(db, 'chefs', chefId), newChef);
                    }
                    await updateDoc(doc(db, 'users', request.userId), { chefId });
                    if (user.uid === request.userId) {
                        setUser((prev) => (prev ? { ...prev, chefId } : null));
                    }
                    await updateDoc(doc(db, 'chefRequests', request.id), {
                        status: 'approved',
                        linkedChefId: chefId,
                        reviewedAt,
                    });
                }}
                onDenyRequest={async (requestId) => {
                    await updateDoc(doc(db, 'chefRequests', requestId), {
                        status: 'denied',
                        reviewedAt: new Date().toISOString(),
                    });
                }}
            />
            <SuggestionModal 
                isOpen={isSuggestionBoxOpen} onClose={() => handleCloseModal(() => setIsSuggestionBoxOpen(false))} user={user} suggestions={suggestions}
                onAdd={async (subject, text, area) => { await addDoc(collection(db, 'suggestions'), { subject, text, area, date: new Date().toISOString(), status: 'new', reporterUserId: user.uid, reporterEmail: user.email }); }}
                onUpdate={async (id, updates) => { await updateDoc(doc(db, 'suggestions', id), updates); }}
            />
            <SuggestionManagementModal 
                isOpen={isSuggestionManagementOpen} onClose={() => handleCloseModal(() => setIsSuggestionManagementOpen(false))} user={user} suggestions={suggestions}
                onUpdateStatus={async (id, status) => { await updateDoc(doc(db, 'suggestions', id), { status }); }}
            />
            <CreateGuidedClientModal
                isOpen={isCreateGuidedClientOpen}
                onClose={() => handleCloseModal(() => setIsCreateGuidedClientOpen(false))}
                currentUser={authUser!}
                consultants={allUsers}
                onCreated={() => setIsGuidedClientsOpen(true)}
            />
            <GuidedClientManagementModal
                isOpen={isGuidedClientsOpen}
                onClose={() => handleCloseModal(() => setIsGuidedClientsOpen(false))}
                currentUser={isImpersonating ? user : (authUser ?? user)}
                allUsers={allUsers}
                onOpenClientHousehold={handleOpenClientHousehold}
                onCreateClient={
                    !isImpersonating && authUser?.role === 'superuser'
                        ? () => {
                            setIsGuidedClientsOpen(false);
                            setIsCreateGuidedClientOpen(true);
                        }
                        : undefined
                }
            />
            {guidedUser && (
                <ConsultationModal
                    isOpen={isConsultationOpen}
                    onClose={() => handleCloseModal(() => setIsConsultationOpen(false))}
                    user={user}
                    allUsers={allUsers.length > 0 ? allUsers : [user]}
                />
            )}
            {canManagePhrases && (
                <ConsultantPhraseAdminModal
                    isOpen={isPhraseAdminOpen}
                    onClose={() => handleCloseModal(() => setIsPhraseAdminOpen(false))}
                    user={user}
                />
            )}
            <GoProModal
                isOpen={isGoProOpen}
                onClose={() => handleCloseModal(() => setIsGoProOpen(false))}
                user={user}
                onUpgraded={async () => {
                    const snap = await getDoc(doc(db, 'users', user.uid));
                    if (snap.exists()) {
                        const fresh = { ...snap.data(), uid: user.uid } as User;
                        setUser(fresh);
                        setAuthUser(fresh);
                        setActiveHouseholdId(fresh.activeHouseholdId || activeHouseholdId);
                        await syncPreferredDietFromUser(user.uid, fresh);
                    }
                }}
            />
            <ContactModal isOpen={isContactOpen} onClose={() => handleCloseModal(() => setIsContactOpen(false))} language={user.language} />
            <HowToModal isOpen={isHowToOpen} onClose={() => handleCloseModal(() => setIsHowToOpen(false))} language={user.language} />
            <TermsOfServiceModal isOpen={isTosOpen} onClose={() => handleCloseModal(() => setIsTosOpen(false))} user={user} />
            <Reminders 
                isOpen={isRemindersOpen} onClose={() => handleCloseModal(() => setIsRemindersOpen(false))} reminders={defrostReminders} language={user.language}
                onAddToCart={async (meatItems) => addIngredientsToActiveCart(meatItems.map(mi => ({ ...mi, isMeat: true })), 'Reminder', 'manual')}
            />
            <NotificationsModal
                isOpen={isNotificationsOpen}
                onClose={() => handleCloseModal(() => setIsNotificationsOpen(false))}
                notifications={notifications}
                language={user.language}
                onMarkAllRead={() => {
                    if (user.uid) {
                        void markAllNotificationsRead(user.uid);
                    }
                }}
                onNotificationClick={(notification) => {
                    void handleNotificationClick(notification);
                }}
            />
            {plannedRecipePreview && (
                <PlannedMealRecipeView
                    recipe={plannedRecipePreview.recipe}
                    plannedServings={plannedRecipePreview.plannedServings}
                    day={plannedRecipePreview.day}
                    mealType={plannedRecipePreview.mealType}
                    weekOffset={plannedRecipePreview.weekOffset}
                    activeHouseholdId={activeHouseholdId}
                    orderedDays={orderedDays as DayOfWeek[]}
                    language={user.language}
                    onClose={(servings) => {
                        setPlannedRecipePreview(null);
                        setAddMealData((previous) => (
                            previous ? { ...previous, currentServings: servings } : previous
                        ));
                    }}
                    onAddIngredientsToCart={async (ings, recipeName) => addIngredientsToActiveCart(ings, recipeName, 'recipe')}
                />
            )}
            {cookingSessionData && (
                <MealCookingSession 
                    recipes={cookingSessionData.meals} allRecipes={recipes} mealType={cookingSessionData.type} language={user.language}
                    canManage={user.role === 'superuser' || householdRole !== 'viewer'}
                    isHidden={isAddRecipeOpen || !!recipeToView}
                    initialRecipeId={cookingSessionData.initialRecipeId}
                    onClose={() => handleCloseModal(() => setCookingSessionData(null))}
                    onAddIngredientsToCart={async (ings, recipeName) => addIngredientsToActiveCart(ings, recipeName, 'recipe')}
                    onViewRecipe={(recipeId) => { const r = recipes.find(x => x.id === recipeId); if (r) setRecipeToView(r); }}
                    currentDay={cookingSessionData.day}
                    mealPlan={{ slotMenuTitles: cookingSessionData.slotMenuTitles ?? mealPlan.slotMenuTitles }}
                    orderedDays={orderedDays as DayOfWeek[]}
                    weekOffset={cookingSessionData.weekOffset}
                    activeHouseholdId={activeHouseholdId}
                    encryptedGeminiKey={!user?.isAiEligible ? user?.encryptedGeminiKey : undefined}
                    currentUserId={user?.uid}
                    onUpdateMealSlotTitle={(day, type, title) => {
                        void handleUpdateMealSlotTitle(
                            day,
                            type,
                            title,
                            cookingSessionData.weekOffset ?? weekOffset,
                        );
                    }}
                />
            )}
        </div>
    </div>
  );
}

// Helper to find day for a meal ID in the current plan structure
function getDayFromMeal(mealPlan: MealPlan, mealId: string, weekOffset: number): DayOfWeek | undefined {
    // This helper is tricky because mealPlan is keyed by week.
    // We only have the *current* week's meal plan loaded in `mealPlan` state usually.
    // If `mealPlan` object structure is simple { Monday: [...], ... }, we can search it.
    for (const day of DAYS_OF_WEEK) {
        if (mealPlan[day]?.some(m => m.id === mealId)) return day;
    }
    return undefined;
}

export default App;
